/**
 * 줄서기(Fila Virtual) 화면 전용 15개 언어 사전 — 디자인 원본 STRINGS 이식.
 *
 * @see #781 / admin#2111 설계 §7 "고객 화면: menu 15-locale 체계 그대로"
 *
 * ## 왜 전역 i18n(`src/i18n`) 이 아니라 화면 로컬 사전인가
 * 전역 사전은 ko/es/en 3개 언어 중심이라 15개 언어를 요구하는 이 화면군을 담지 못한다.
 * 줄서기 문구는 이 화면 밖에서 재사용되지 않으므로 화면 단위로 분리해 전역 사전의
 * 언어 커버리지 불균형을 옮겨오지 않는다.
 *
 * ## 강조(bold) 구간
 * 디자인의 `teamsInfo` / `teAsignamos` 는 문장 일부만 굵게 표시한다. 사전에서 JSX 를
 * 반환하면 문자열 사전이 컴포넌트 파일이 되므로, 대신 `WaitlistSegment[]` 를 반환하고
 * 렌더는 `<WaitlistSegments>` 가 담당한다 (강조 위치는 언어마다 다르므로 사전이 소유).
 */
import type { Locale } from '@/config/locales';

/** 문장 조각 — `bold` 인 조각만 굵게 렌더된다. */
export interface WaitlistSegment {
  text: string;
  bold?: boolean;
  /** 본문색 강조 (디자인의 `Bf` — primary 배경이 아닌 카드 위 강조). */
  strong?: boolean;
}

/**
 * 국가코드 + 자리수 검증표 — 디자인 원본 COUNTRIES 그대로.
 *
 * `len: null` 은 국가별 고정 자리수가 없다는 뜻으로, 6~15자리 범용 규칙을 적용한다
 * (서버 JoinWaitlistRequest 의 `regex:/^\d{6,14}$/` 와 정합 — 상한은 아래 참고).
 */
export interface WaitlistCountry {
  /** 국가 번호 (`+52`). */
  dial: string;
  /** 표시명. */
  name: string;
  /** 고정 자리수. null 이면 가변. */
  len: number | null;
}

export const WAITLIST_COUNTRIES: WaitlistCountry[] = [
  { dial: '+52', name: 'México', len: 10 },
  { dial: '+1', name: 'USA / Canadá', len: 10 },
  { dial: '+34', name: 'España', len: 9 },
  { dial: '+57', name: 'Colombia', len: 10 },
  { dial: '+54', name: 'Argentina', len: 10 },
  { dial: '+55', name: 'Brasil', len: 11 },
  { dial: '+44', name: 'UK', len: null },
  { dial: '+33', name: 'France', len: 9 },
  { dial: '+49', name: 'Deutschland', len: null },
  { dial: '+82', name: '한국', len: null },
  { dial: '+81', name: '日本', len: null },
  { dial: '+86', name: '中国', len: 11 },
];

export const DEFAULT_DIAL_CODE = '+52';

/**
 * 가변 자리수 국가의 허용 범위.
 *
 * 하한은 디자인 원본과 동일한 6. 상한은 디자인의 15 가 아니라 **14** 다 —
 * 서버 검증이 `^\d{6,14}$` 이므로 15자리를 통과시키면 클라이언트만 유효 판정하고
 * 서버에서 422 가 난다(silent 불일치). 좁은 쪽에 맞춘다.
 *
 * @see olulo-mx-admin app/Http/Requests/Waitlist/JoinWaitlistRequest.php
 */
export const VARIABLE_PHONE_MIN_DIGITS = 6;
export const VARIABLE_PHONE_MAX_DIGITS = 14;

/** 줄서기 화면 문구 계약. */
export interface WaitlistStrings {
  subWaiting: (ticket: string, party: number) => string;
  filaAbierta: string;
  filaCerrada: string;
  cerradaDesc: string;
  qrInactivo: string;
  qrInactivoDesc: string;
  joinTitle: string;
  joinLead: string;
  howMany: string;
  personas: string;
  telefono: string;
  telPh: string;
  telError: string;
  telHelp: string;
  notifyNote: string;
  teamsInfo: (teams: number, minutes: number) => WaitlistSegment[];
  joinCta: string;
  joining: string;
  warnMax: (max: number) => string;
  duplicado: string;
  errorGenerico: string;
  reintentar: string;
  tuTurno: string;
  enEspera: string;
  equipos: (position: number) => string;
  casiTurno: string;
  teUniste: string;
  tuMesa: string;
  esperaAprox: (minutes: number) => string;
  reservadoPara: string;
  nPersonas: (n: number) => string;
  aviso: string;
  smsRespaldo: string;
  tip: string;
  salirFila: string;
  dlgTitle: string;
  dlgDesc: (ticket: string, party: number) => string;
  seguir: string;
  siSalir: string;
  esTuTurno: string;
  mesaLista: string;
  turno: string;
  llamado: string;
  personasLbl: string;
  veA: string;
  recepcion: string;
  guardamos: string;
  yaLlegue: string;
  noPodre: string;
  bienvenido: string;
  teAsignamos: (table: string, party: number) => WaitlistSegment[];
  atendido: (ticket: string) => string;
  verMenu: string;
  volverInicio: string;
  salisteFila: string;
  liberamos: string;
  volverUnirme: string;
  idioma: string;
  gpsNote: string;
  gpsChecking: string;
  gpsFar: string;
  gpsUnavailable: string;
  cargando: string;
  partyReadError: string;
  partyReadStale: string;

  // --- 줄서기 2종 확장(테이블+픽업) — 선택 화면 + 픽업 큐 전용 (#794) ---
  /** 선택 화면(둘 다 열린 매장): "¿Cómo prefieres?" 류 타이틀. */
  chooseTitle: string;
  chooseLead: string;
  modeTable: string;
  modeTableDesc: string;
  modePickup: string;
  modePickupDesc: string;
  pkJoinTitle: string;
  pkJoinLead: string;
  pkRefHelp: string;
  pkTuTurno: string;
  pkWaitBadge: string;
  pkReservado: string;
  pkEspera: (minutes: number) => string;
  /** 픽업 대기 화면(WaitingScreen) 부제 — 테이블용 "인원" 문구 대신 순번/대기만 표기(#794 Task 9 시정). */
  pkSubWaiting: (ticket: string) => string;
  /** 픽업 대기 화면 이탈 확인 다이얼로그 설명 — 테이블용 "좌석" 문구 대신 순번만 표기. */
  pkDlgDesc: (ticket: string) => string;
  pkReadyTitle: string;
  pkReadyLead: string;
  pkGoTo: string;
  pkGuardamos: string;
  pkArrived: string;
  pkDoneTitle: string;
  pkNumLabel: string;
  pkDoneLead: string;
  pkAtendido: (ticket: string) => string;
  pkOrderNumLabel: string;
  pkPrepTitle: string;
  pkPrepLead: string;
  pkPrepBadge: string;
  pkPrepEta: (minutes: number) => string;
  pkReady2Title: string;
  pkReady2Lead: string;
  pkCollect: string;
  pkDeliveredTitle: string;
  pkDeliveredLead: string;
  menuDigital: string;
  menuPdf: string;
  cancelar: string;
  verUbicacion: string;
  /**
   * 픽업 게이트 called 화면 "앱에서 주문하기" 버튼(admin#2172/menu#804) — 게이트
   * 매장(`store_meta.pickup_requires_waitlist`)에서만 노출.
   */
  pkOrderInApp: string;
  /**
   * 마케팅 동의 접이식 UI(#882) 펼침 토글의 보조기술 전용 꼬리말 — 화면에는 보이지 않는다.
   *
   * 체크박스(`aria-label` = 요약)와 토글이 같은 이름을 가지면 스크린리더 순회에서
   * 두 컨트롤을 구분할 수 없다. 요약 뒤에 이 문구를 덧붙여 "확인란"과 "전문 펼침 단추"를
   * 갈라준다(동의 문구 자체가 아니므로 서버 해시 대상이 아니다).
   */
  mcShowDetails: string;
  /**
   * 마케팅 동의 셀프 철회(admin#2472) — 동의를 기록한 고객에게만 노출되는 조각.
   *
   * 등록 폼의 동의 문구(`marketing_consent.text`)는 서버가 locale 별로 소유하지만,
   * 철회 UI 문구는 증거로 남지 않는 화면 안내라 다른 줄서기 문구와 같이 여기서 관리한다.
   */
  mcWithdrawLink: string;
  /** 철회 확인 다이얼로그 제목 — 이탈(dlgTitle)과 동일한 1회 확인 UX. */
  mcWithdrawTitle: string;
  /**
   * 철회 확인 문구 — **범위는 이 줄서기가 아니라 연락처 전역**(admin#2491 동의 v2).
   *
   * 서버의 셀프 철회는 이 번호(vault)의 미철회 동의 증거를 전부 철회하고 전역 억제를
   * 세운다 — OLULO 와 참여 매장 전체의 WhatsApp·SMS 마케팅이 함께 끊긴다. 문구가
   * "이 줄서기에서만"이라고 읽히면 고객이 한 매장만 끄는 줄 알고 전체를 끄게 되므로,
   * 15개 언어 모두 "이 번호" 범위와 재동의 경로(줄서기 등록 시 다시 선택)를 말한다.
   */
  mcWithdrawDesc: string;
  mcWithdrawCta: string;
  /**
   * 철회 완료(또는 서버에 동의 증거가 없어 철회할 것이 없던 경우) 안내.
   *
   * 확인 문구와 같은 이유로 범위는 연락처 전역이다 — {@link mcWithdrawDesc} 참고.
   */
  mcWithdrawDone: string;
  mcWithdrawError: string;
  /**
   * 철회를 **처리할 수 없었을 때**의 전용 안내 — 409 `CONSENT_WITHDRAWAL_UNAVAILABLE`
   * (admin#2493 / #884).
   *
   * 일반 오류(`mcWithdrawError`)와 갈라 쓰는 이유: 이 응답은 재시도해도 같은 결과가
   * 나올 수 있는 상태(연락처 신원 모호·내부 불일치)라 "다시 시도해 주세요"만으로는
   * 고객이 철회 수단을 잃는다. 개인정보 통제권을 막다른 길로 두지 않도록 사람이
   * 처리할 수 있는 다른 경로(이메일·WhatsApp)를 함께 안내한다.
   *
   * 이메일 주소와 채널명(WhatsApp), 답장 키워드(BAJA)는 서버가 인식하는 리터럴이므로
   * 15개 언어 모두 원문 표기를 유지한다.
   */
  mcWithdrawUnavailable: string;
  /**
   * 매장 팔로우 토글 라벨 — "이 매장 소식 받기" (admin#2496 / #885).
   *
   * 전역 철회(`mcWithdraw*`)와 범위가 다르다. 철회는 이 번호의 모든 매장·OLULO 수신을
   * 끊지만, 이 토글은 **이 파티의 매장 한 곳**만 켜고 끈다. 문구가 "프로모션 받기"처럼
   * 범위를 흐리면 고객이 전역 설정으로 오해하므로 15개 언어 모두 "이 매장" 을 말한다.
   */
  storeFollowLabel: string;
  /**
   * OFF 상태 보조 문구 — **무엇이 끊기고 무엇이 계속 오는지**를 함께 말한다.
   *
   * 순번·호출·픽업 안내는 광고가 아니라 서비스 메시지라 억제 대상이 아니다(follow
   * 설계 §5). 이 문장이 없으면 고객은 토글을 끄면 순번 알림도 끊긴다고 믿고 끄지
   * 못하거나, 껐다가 순번을 놓칠까 불안해한다.
   */
  storeFollowOffHint: string;
  /** ON 상태 보조 문구 — 어디로(이 번호) 무엇이(쿠폰·소식) 오는지. */
  storeFollowOnHint: string;
  /** 토글 저장 실패(네트워크·5xx·409 `STORE_FOLLOW_UNAVAILABLE`) — 재시도 안내. */
  storeFollowError: string;
  /**
   * 409 `STORE_FOLLOW_RECONSENT_REQUIRED` 전용 안내 — 토글로는 켤 수 없는 경우.
   *
   * v1 시절의 매장 프라이버시 철회가 활성이면 약한 사유(선호 토글)로 닫을 수 없다
   * (억제 강도 사다리, follow 설계 §1.4). 해제 경로는 그 매장의 **새 동의**뿐이므로
   * 일반 오류와 갈라 "등록할 때 동의를 다시 선택" 이라는 실제 경로를 알려준다.
   */
  storeFollowReconsent: string;
}

export const WAITLIST_STRINGS: Record<Locale, WaitlistStrings> = {
  es: {
    subWaiting: (t, n) => `Turno ${t} · ${n} personas`,
    filaAbierta: 'Fila abierta',
    filaCerrada: 'Fila cerrada',
    cerradaDesc: 'Por ahora no estamos tomando registros. Acude directamente con el anfitrión.',
    qrInactivo: 'Código QR no válido',
    qrInactivoDesc: 'Este código ya no está activo. Pide al anfitrión el código vigente.',
    joinTitle: 'Únete a la fila',
    joinLead: 'Espera desde donde quieras. Te avisamos aquí y por WhatsApp cuando tu mesa esté lista.',
    howMany: '¿Cuántas personas?',
    personas: 'Personas',
    telefono: 'Teléfono',
    telPh: '55 0000 0000',
    telError: 'Ingresa un número de teléfono válido.',
    telHelp: 'De preferencia, un número con WhatsApp.',
    notifyNote: 'Te avisaremos por WhatsApp. Si no se entrega, te enviamos un SMS.',
    teamsInfo: (n, m) => [
      { text: `${n} equipos`, bold: true },
      { text: ' en fila · espera aprox. ' },
      { text: `${m} min`, bold: true },
    ],
    joinCta: 'Unirme a la fila',
    joining: 'Registrando…',
    warnMax: (max) => `El máximo por registro es de ${max} personas. Para grupos más grandes, acude directamente con el anfitrión.`,
    duplicado: 'Ya tienes un registro activo con este teléfono.',
    errorGenerico: 'No pudimos completar la acción. Inténtalo de nuevo.',
    reintentar: 'Reintentar',
    tuTurno: 'Tu turno',
    enEspera: 'En espera',
    equipos: (n) => (n === 1 ? 'equipo delante de ti' : 'equipos delante de ti'),
    casiTurno: '¡Casi es tu turno!',
    teUniste: 'Te uniste',
    tuMesa: 'Tu mesa',
    esperaAprox: (m) => `Espera aprox. ${m} min`,
    reservadoPara: 'Reservado para',
    nPersonas: (n) => `${n} personas`,
    aviso: 'Aviso',
    smsRespaldo: 'SMS de respaldo',
    tip: 'Mantén esta pantalla a la mano. Cuando falten pocos minutos te avisaremos para que te acerques.',
    salirFila: 'Salir de la fila',
    dlgTitle: '¿Salir de la fila?',
    dlgDesc: (t, n) => `Perderás tu turno ${t} y ${n} lugares reservados. Tendrás que registrarte de nuevo al final de la fila.`,
    seguir: 'Seguir esperando',
    siSalir: 'Sí, salir',
    esTuTurno: '¡Es tu turno!',
    mesaLista: 'Tu mesa está lista. Preséntate con el anfitrión en la entrada.',
    turno: 'Turno',
    llamado: 'Llamado',
    personasLbl: 'Personas',
    veA: 'Ve a',
    recepcion: 'Recepción · entrada',
    guardamos: 'Guardamos tu lugar',
    yaLlegue: 'Ya llegué',
    noPodre: 'No podré llegar',
    bienvenido: '¡Bienvenido!',
    teAsignamos: (mesa, n) => [
      { text: 'Te asignamos la ' },
      { text: `Mesa ${mesa}`, strong: true },
      { text: ` para ${n} personas.` },
    ],
    atendido: (t) => `Turno ${t} · atendido`,
    verMenu: 'Ver el menú',
    volverInicio: 'Volver al inicio',
    salisteFila: 'Saliste de la fila',
    liberamos: 'Liberamos tu lugar. Puedes volver a unirte cuando quieras.',
    volverUnirme: 'Volver a unirme',
    idioma: 'Idioma',
    gpsNote: 'Verificaremos tu ubicación al confirmar.',
    gpsChecking: 'Verificando tu ubicación…',
    gpsFar: 'Estás demasiado lejos del restaurante. Acércate para confirmar tu llegada.',
    gpsUnavailable: 'No pudimos verificar tu ubicación. Preséntate con el anfitrión en la entrada.',
    cargando: 'Cargando…',
    partyReadError: 'No pudimos cargar tu estado de espera.',
    partyReadStale: 'No pudimos actualizar. Mostramos el último estado recibido y volveremos a intentarlo.',
    chooseTitle: '¿Cómo prefieres?',
    chooseLead: 'Elige cómo quieres tu experiencia hoy.',
    modeTable: 'Comer en el local',
    modeTableDesc: 'Únete a la fila para una mesa.',
    modePickup: 'Ordenar para llevar',
    modePickupDesc: 'Haz fila para pasar a ordenar en caja.',
    pkJoinTitle: 'Fila para ordenar',
    pkJoinLead: 'Te avisamos aquí y por WhatsApp cuando puedas pasar a ordenar.',
    pkRefHelp: 'Guardamos tu lugar. Cuando sea tu turno podrás pasar a ordenar en caja.',
    pkTuTurno: 'Tu turno',
    pkWaitBadge: 'En fila',
    pkEspera: (m) => `Tu turno en aprox. ${m} min`,
    pkReservado: 'Turno',
    pkSubWaiting: (t) => `Turno ${t} · en fila`,
    pkDlgDesc: (t) => `Perderás tu turno ${t} para ordenar. Tendrás que registrarte de nuevo al final de la fila.`,
    pkReadyTitle: '¡Ya puedes ordenar!',
    pkReadyLead: 'Pasa a la caja y muestra tu turno para hacer tu pedido.',
    pkGoTo: 'Caja · mostrador',
    pkGuardamos: 'Guardamos tu turno',
    pkArrived: 'Voy a ordenar',
    pkDoneTitle: '¡Listo para ordenar!',
    pkNumLabel: 'Tu número de turno',
    pkDoneLead: 'Muestra este número al mesero para hacer tu pedido.',
    pkAtendido: (t) => `Turno ${t} · atendido`,
    pkOrderNumLabel: 'Número de pedido',
    pkPrepTitle: '¡Pedido registrado!',
    pkPrepLead: 'Comienza la espera de recogida. Te avisamos por WhatsApp cuando esté listo.',
    pkPrepBadge: 'Preparando',
    pkPrepEta: (m) => `Listo en aprox. ${m} min`,
    pkReady2Title: '¡Listo para recoger!',
    pkReady2Lead: 'Recoge tu pedido en el mostrador y muestra tu número.',
    pkCollect: 'Ya lo recogí',
    pkDeliveredTitle: '¡Buen provecho!',
    pkDeliveredLead: 'Gracias por tu pedido. Vuelve pronto.',
    menuDigital: 'Menú digital',
    menuPdf: 'Menú en PDF',
    cancelar: 'Cancelar',
    verUbicacion: 'Ver ubicación',
    pkOrderInApp: 'Ordenar en la app',
    mcShowDetails: 'Ver detalles',
    mcWithdrawLink: 'Retirar consentimiento de marketing',
    mcWithdrawTitle: '¿Retirar tu consentimiento?',
    mcWithdrawDesc: 'Dejarás de recibir promociones y novedades de OLULO y sus comercios por WhatsApp y SMS en este número. Puedes volver a aceptarlas al registrarte en una fila. No afecta tu lugar en la fila ni los avisos de tu turno.',
    mcWithdrawCta: 'Sí, retirar',
    mcWithdrawDone: 'Listo. Ya no recibirás promociones de OLULO en este número.',
    mcWithdrawError: 'No pudimos retirar tu consentimiento. Inténtalo de nuevo.',
    mcWithdrawUnavailable: 'No pudimos procesar la baja en este momento. Escríbenos a hola@olulo.com.mx o responde BAJA en el chat de WhatsApp.',
    storeFollowLabel: 'Recibir promociones de este comercio',
    storeFollowOffHint: 'No recibirás promociones de este comercio. Seguirás recibiendo los avisos de tu turno.',
    storeFollowOnHint: 'Recibirás cupones y novedades de este comercio en este número.',
    storeFollowError: 'No pudimos guardar tu preferencia. Inténtalo de nuevo.',
    storeFollowReconsent: 'Para volver a recibir promociones de este comercio, acepta el consentimiento al registrarte en la fila.',
  },
  en: {
    subWaiting: (t, n) => `Ticket ${t} · ${n} guests`,
    filaAbierta: 'Waitlist open',
    filaCerrada: 'Waitlist closed',
    cerradaDesc: "We're not taking sign-ups right now. Please see the host directly.",
    qrInactivo: 'Invalid QR code',
    qrInactivoDesc: 'This code is no longer active. Ask the host for the current one.',
    joinTitle: 'Join the waitlist',
    joinLead: "Wait from anywhere. We'll notify you here and on WhatsApp when your table is ready.",
    howMany: 'How many guests?',
    personas: 'Guests',
    telefono: 'Phone',
    telPh: '55 0000 0000',
    telError: 'Enter a valid phone number.',
    telHelp: 'Preferably a number with WhatsApp.',
    notifyNote: "We'll notify you on WhatsApp. If it can't be delivered, we'll send an SMS.",
    teamsInfo: (n, m) => [
      { text: `${n} groups`, bold: true },
      { text: ' waiting · approx. ' },
      { text: `${m} min`, bold: true },
    ],
    joinCta: 'Join the waitlist',
    joining: 'Signing up…',
    warnMax: (max) => `The maximum per sign-up is ${max} guests. For larger groups, please see the host directly.`,
    duplicado: 'You already have an active sign-up with this phone number.',
    errorGenerico: "We couldn't complete that. Please try again.",
    reintentar: 'Try again',
    tuTurno: 'Your ticket',
    enEspera: 'Waiting',
    equipos: (n) => (n === 1 ? 'group ahead of you' : 'groups ahead of you'),
    casiTurno: 'Almost your turn!',
    teUniste: 'You joined',
    tuMesa: 'Your table',
    esperaAprox: (m) => `Approx. ${m} min wait`,
    reservadoPara: 'Reserved for',
    nPersonas: (n) => `${n} guests`,
    aviso: 'Alerts',
    smsRespaldo: 'SMS fallback',
    tip: "Keep this screen handy. We'll alert you a few minutes before so you can head over.",
    salirFila: 'Leave the waitlist',
    dlgTitle: 'Leave the waitlist?',
    dlgDesc: (t, n) => `You'll lose ticket ${t} and ${n} reserved seats. You'd have to sign up again at the back of the line.`,
    seguir: 'Keep waiting',
    siSalir: 'Yes, leave',
    esTuTurno: "It's your turn!",
    mesaLista: 'Your table is ready. Check in with the host at the entrance.',
    turno: 'Ticket',
    llamado: 'Called',
    personasLbl: 'Guests',
    veA: 'Go to',
    recepcion: 'Reception · entrance',
    guardamos: "We're holding your spot",
    yaLlegue: "I'm here",
    noPodre: "I can't make it",
    bienvenido: 'Welcome!',
    teAsignamos: (mesa, n) => [
      { text: "We've assigned you " },
      { text: `Table ${mesa}`, strong: true },
      { text: ` for ${n} guests.` },
    ],
    atendido: (t) => `Ticket ${t} · seated`,
    verMenu: 'View the menu',
    volverInicio: 'Back to start',
    salisteFila: 'You left the waitlist',
    liberamos: "We've released your spot. You can rejoin whenever you like.",
    volverUnirme: 'Rejoin the waitlist',
    idioma: 'Language',
    gpsNote: "We'll verify your location when you confirm.",
    gpsChecking: 'Checking your location…',
    gpsFar: "You're too far from the restaurant. Get closer to confirm your arrival.",
    gpsUnavailable: "We couldn't verify your location. Please check in with the host at the entrance.",
    cargando: 'Loading…',
    partyReadError: "We couldn't load your waitlist status.",
    partyReadStale: "We couldn't update. Showing the last received status; we'll try again.",
    chooseTitle: 'How would you like it?',
    chooseLead: 'Choose how you want your experience today.',
    modeTable: 'Dine in',
    modeTableDesc: 'Join the line for a table.',
    modePickup: 'Order for pickup',
    modePickupDesc: 'Line up to order at the counter.',
    pkJoinTitle: 'Order line',
    pkJoinLead: "We'll notify you here and on WhatsApp when it's your turn to order.",
    pkRefHelp: "We're holding your spot. When it's your turn you'll order at the counter.",
    pkTuTurno: 'Your turn',
    pkWaitBadge: 'In line',
    pkEspera: (m) => `Your turn in approx. ${m} min`,
    pkReservado: 'Turn',
    pkSubWaiting: (t) => `Turn ${t} · in line`,
    pkDlgDesc: (t) => `You'll lose turn ${t} to order. You'd have to sign up again at the back of the line.`,
    pkReadyTitle: 'You can order now!',
    pkReadyLead: 'Go to the counter and show your turn to place your order.',
    pkGoTo: 'Order counter',
    pkGuardamos: "We're holding your turn",
    pkArrived: "I'm ordering",
    pkDoneTitle: 'Ready to order!',
    pkNumLabel: 'Your turn number',
    pkDoneLead: 'Show this number to the server to place your order.',
    pkAtendido: (t) => `Turn ${t} · done`,
    pkOrderNumLabel: 'Order number',
    pkPrepTitle: 'Order placed!',
    pkPrepLead: "The pickup wait has started. We'll notify you on WhatsApp when it's ready.",
    pkPrepBadge: 'Preparing',
    pkPrepEta: (m) => `Ready in approx. ${m} min`,
    pkReady2Title: 'Ready for pickup!',
    pkReady2Lead: 'Collect your order at the counter and show your number.',
    pkCollect: 'I picked it up',
    pkDeliveredTitle: 'Enjoy!',
    pkDeliveredLead: 'Thanks for your order. See you soon.',
    menuDigital: 'Digital menu',
    menuPdf: 'PDF menu',
    cancelar: 'Cancel',
    verUbicacion: 'View location',
    pkOrderInApp: 'Order in the app',
    mcShowDetails: 'Show details',
    mcWithdrawLink: 'Withdraw marketing consent',
    mcWithdrawTitle: 'Withdraw your consent?',
    mcWithdrawDesc: "You'll stop receiving promotions and news from OLULO and its participating venues by WhatsApp and SMS on this number. You can accept them again when you join a waitlist. This does not affect your place in the line or your turn notifications.",
    mcWithdrawCta: 'Yes, withdraw',
    mcWithdrawDone: "Done. You'll no longer receive OLULO promotions on this number.",
    mcWithdrawError: "We couldn't withdraw your consent. Please try again.",
    mcWithdrawUnavailable: "We couldn't process your withdrawal right now. Write to us at hola@olulo.com.mx or reply BAJA in the WhatsApp chat.",
    storeFollowLabel: 'Get promotions from this venue',
    storeFollowOffHint: "You won't get promotions from this venue. You'll still get your turn notifications.",
    storeFollowOnHint: "You'll get coupons and news from this venue on this number.",
    storeFollowError: "We couldn't save your preference. Please try again.",
    storeFollowReconsent: 'To receive promotions from this venue again, accept the consent when you join the line.',
  },
  ko: {
    subWaiting: (t, n) => `대기번호 ${t} · ${n}명`,
    filaAbierta: '대기 접수중',
    filaCerrada: '대기 접수 중지',
    cerradaDesc: '지금은 대기 등록을 받지 않습니다. 매장 직원에게 직접 문의해 주세요.',
    qrInactivo: '사용할 수 없는 QR입니다',
    qrInactivoDesc: '이 QR은 더 이상 활성 상태가 아닙니다. 매장 직원에게 최신 QR을 요청해 주세요.',
    joinTitle: '대기줄 등록',
    joinLead: '어디서든 기다리세요. 자리가 준비되면 이 화면과 WhatsApp으로 알려드립니다.',
    howMany: '몇 분이신가요?',
    personas: '인원',
    telefono: '전화번호',
    telPh: '010 0000 0000',
    telError: '올바른 전화번호를 입력하세요.',
    telHelp: '가능하면 WhatsApp이 되는 번호로.',
    notifyNote: 'WhatsApp으로 알려드립니다. 전송이 안 되면 문자(SMS)로 대신 보내드려요.',
    teamsInfo: (n, m) => [
      { text: '대기 ' },
      { text: `${n}팀`, bold: true },
      { text: ' · 예상 ' },
      { text: `약 ${m}분`, bold: true },
    ],
    joinCta: '대기줄 등록하기',
    joining: '등록 중…',
    warnMax: (max) => `1회 등록 최대 인원은 ${max}명입니다. 더 큰 단체는 매장 직원에게 직접 문의해 주세요.`,
    duplicado: '이 번호로 이미 대기 등록이 되어 있어요.',
    errorGenerico: '요청을 완료하지 못했어요. 다시 시도해 주세요.',
    reintentar: '다시 시도',
    tuTurno: '내 대기번호',
    enEspera: '대기중',
    equipos: () => '팀 앞에 있어요',
    casiTurno: '곧 입장 차례예요!',
    teUniste: '등록 완료',
    tuMesa: '내 자리',
    esperaAprox: (m) => `예상 대기 약 ${m}분`,
    reservadoPara: '예약 인원',
    nPersonas: (n) => `${n}명`,
    aviso: '알림',
    smsRespaldo: 'SMS 백업',
    tip: '이 화면을 켜 두세요. 차례가 가까워지면 입장 안내를 보내드립니다.',
    salirFila: '대기 취소하기',
    dlgTitle: '대기를 취소할까요?',
    dlgDesc: (t, n) => `대기번호 ${t}와 예약한 ${n}자리가 사라집니다. 다시 등록하면 줄 맨 뒤로 갑니다.`,
    seguir: '계속 기다리기',
    siSalir: '네, 취소할게요',
    esTuTurno: '입장하실 차례예요!',
    mesaLista: '자리가 준비됐습니다. 입구의 안내 직원에게 알려주세요.',
    turno: '대기번호',
    llamado: '호출됨',
    personasLbl: '인원',
    veA: '이동',
    recepcion: '입구 · 안내데스크',
    guardamos: '자리 보관 시간',
    yaLlegue: '도착했어요',
    noPodre: '갈 수 없어요',
    bienvenido: '환영합니다!',
    teAsignamos: (mesa, n) => [
      { text: `${mesa}번 테이블`, strong: true },
      { text: `을 ${n}명으로 배정했어요.` },
    ],
    atendido: (t) => `대기번호 ${t} · 입장완료`,
    verMenu: '메뉴 보기',
    volverInicio: '처음으로',
    salisteFila: '대기를 취소했어요',
    liberamos: '자리를 반환했습니다. 언제든 다시 등록할 수 있어요.',
    volverUnirme: '다시 등록하기',
    idioma: '언어',
    gpsNote: '확인 시 GPS로 위치를 인증합니다.',
    gpsChecking: '위치 확인 중…',
    gpsFar: '매장에서 너무 멀리 있습니다. 가까이 이동한 뒤 도착을 확인해 주세요.',
    gpsUnavailable: '위치를 확인하지 못했어요. 입구의 안내 직원에게 문의해 주세요.',
    cargando: '불러오는 중…',
    partyReadError: '대기 상태를 불러오지 못했어요.',
    partyReadStale: '상태를 갱신하지 못해 마지막으로 받은 상태를 보여주고 있어요. 다시 시도할게요.',
    chooseTitle: '어떻게 이용하시나요?',
    chooseLead: '오늘 원하는 방식을 선택하세요.',
    modeTable: '매장 식사',
    modeTableDesc: '테이블 대기줄에 등록합니다.',
    modePickup: '포장 주문',
    modePickupDesc: '카운터에서 주문하기 위해 줄을 섭니다.',
    pkJoinTitle: '주문 대기줄',
    pkJoinLead: '주문하실 차례가 되면 이 화면과 WhatsApp으로 알려드립니다.',
    pkRefHelp: '줄 서지 않아도 순번을 맡아 드립니다. 차례가 되면 카운터에서 주문하실 수 있어요.',
    pkTuTurno: '내 순번',
    pkWaitBadge: '대기중',
    pkEspera: (m) => `약 ${m}분 후 주문 차례`,
    pkReservado: '순번',
    pkSubWaiting: (t) => `순번 ${t} · 대기중`,
    pkDlgDesc: (t) => `순번 ${t}가 사라집니다. 다시 등록하면 줄 맨 뒤로 갑니다.`,
    pkReadyTitle: '이제 주문 가능합니다!',
    pkReadyLead: '카운터로 오셔서 순번을 보여주고 주문해 주세요.',
    pkGoTo: '주문 카운터',
    pkGuardamos: '순번 보관 시간',
    pkArrived: '주문하러 가기',
    pkDoneTitle: '주문 준비 완료!',
    pkNumLabel: '대기번호',
    pkDoneLead: '이 대기번호를 종업원에게 보여주고 주문하세요.',
    pkAtendido: (t) => `순번 ${t} · 완료`,
    pkOrderNumLabel: '주문번호',
    pkPrepTitle: '주문이 접수됐어요!',
    pkPrepLead: '픽업 대기가 시작됐습니다. 준비가 완료되면 WhatsApp으로 알려드려요.',
    pkPrepBadge: '준비중',
    pkPrepEta: (m) => `약 ${m}분 후 준비 완료`,
    pkReady2Title: '픽업하세요!',
    pkReady2Lead: '카운터에서 주문번호를 보여주고 받아가세요.',
    pkCollect: '픽업 완료',
    pkDeliveredTitle: '맛있게 드세요!',
    pkDeliveredLead: '주문해 주셔서 감사합니다.',
    menuDigital: '디지털 메뉴',
    menuPdf: 'PDF 메뉴',
    cancelar: '취소',
    verUbicacion: '위치 보기',
    pkOrderInApp: '앱에서 주문하기',
    mcShowDetails: '전문 보기',
    mcWithdrawLink: '마케팅 수신 동의 철회',
    mcWithdrawTitle: '동의를 철회할까요?',
    mcWithdrawDesc: '이 번호로는 OLULO와 참여 매장의 프로모션·소식을 WhatsApp·SMS로 더 이상 받지 않습니다. 다시 받으려면 줄서기에 등록할 때 다시 선택하면 됩니다. 줄서기 순번과 호출 안내에는 영향이 없습니다.',
    mcWithdrawCta: '네, 철회할래요',
    mcWithdrawDone: '완료했어요. 이제 이 번호로는 OLULO 프로모션을 받지 않습니다.',
    mcWithdrawError: '동의를 철회하지 못했습니다. 다시 시도해 주세요.',
    mcWithdrawUnavailable: '지금은 철회를 처리하지 못했습니다. hola@olulo.com.mx 으로 메일을 보내거나 WhatsApp 채팅에서 BAJA 라고 답장해 주세요.',
    storeFollowLabel: '이 매장 소식 받기',
    storeFollowOffHint: '이 매장의 프로모션을 받지 않습니다. 순번·호출 안내는 계속 받아요.',
    storeFollowOnHint: '이 번호로 이 매장의 쿠폰·소식을 받습니다.',
    storeFollowError: '설정을 저장하지 못했어요. 다시 시도해 주세요.',
    storeFollowReconsent: '이 매장 소식을 다시 받으려면 줄서기 등록 때 동의를 다시 선택해 주세요.',
  },
  ja: {
    subWaiting: (t, n) => `受付番号 ${t} · ${n}名`,
    filaAbierta: '受付中',
    filaCerrada: '受付停止中',
    cerradaDesc: '現在、順番待ちの受付を停止しています。スタッフに直接お問い合わせください。',
    qrInactivo: '無効なQRコードです',
    qrInactivoDesc: 'このコードは無効になりました。スタッフに最新のコードをお尋ねください。',
    joinTitle: '順番待ちに登録',
    joinLead: 'どこにいても待てます。席の準備ができたら、この画面とWhatsAppでお知らせします。',
    howMany: '何名様ですか？',
    personas: '人数',
    telefono: '電話番号',
    telPh: '090 0000 0000',
    telError: '有効な電話番号を入力してください。',
    telHelp: 'できればWhatsApp利用番号を。',
    notifyNote: 'WhatsAppでお知らせします。届かない場合はSMSで送ります。',
    teamsInfo: (n, m) => [
      { text: `${n}組`, bold: true },
      { text: '待ち · 目安 ' },
      { text: `約${m}分`, bold: true },
    ],
    joinCta: '順番待ちに登録',
    joining: '登録中…',
    warnMax: (max) => `1回の登録は最大${max}名です。それ以上の団体はスタッフに直接お問い合わせください。`,
    duplicado: 'この電話番号ではすでに登録済みです。',
    errorGenerico: '処理できませんでした。もう一度お試しください。',
    reintentar: '再試行',
    tuTurno: '受付番号',
    enEspera: '待機中',
    equipos: () => '組があなたの前にいます',
    casiTurno: 'まもなくご案内します！',
    teUniste: '登録済み',
    tuMesa: 'あなたの席',
    esperaAprox: (m) => `待ち時間 約${m}分`,
    reservadoPara: 'ご予約人数',
    nPersonas: (n) => `${n}名`,
    aviso: '通知',
    smsRespaldo: 'SMSの予備',
    tip: 'この画面を開いたままにしてください。順番が近づいたらお知らせします。',
    salirFila: '順番待ちをやめる',
    dlgTitle: '順番待ちをやめますか？',
    dlgDesc: (t, n) => `受付番号${t}と${n}名分の予約が取り消されます。再登録すると最後尾になります。`,
    seguir: '待ち続ける',
    siSalir: 'はい、やめます',
    esTuTurno: 'ご案内します！',
    mesaLista: 'お席の準備ができました。入口のスタッフにお声がけください。',
    turno: '受付番号',
    llamado: '呼び出し',
    personasLbl: '人数',
    veA: '向かう先',
    recepcion: '受付 · 入口',
    guardamos: 'お席をお取りしています',
    yaLlegue: '到着しました',
    noPodre: '行けません',
    bienvenido: 'ようこそ！',
    teAsignamos: (mesa, n) => [
      { text: `テーブル${mesa}`, strong: true },
      { text: `を${n}名でご用意しました。` },
    ],
    atendido: (t) => `受付番号 ${t} · 案内済み`,
    verMenu: 'メニューを見る',
    volverInicio: '最初に戻る',
    salisteFila: '順番待ちをやめました',
    liberamos: 'お席を解放しました。いつでも再登録できます。',
    volverUnirme: 'もう一度登録する',
    idioma: '言語',
    gpsNote: '確認時に位置情報を認証します。',
    gpsChecking: '位置を確認しています…',
    gpsFar: 'レストランから遠すぎます。近づいて到着を確認してください。',
    gpsUnavailable: '位置を確認できませんでした。入口のスタッフにお声がけください。',
    cargando: '読み込み中…',
    partyReadError: '待機状況を読み込めませんでした。',
    partyReadStale: '更新できなかったため、最後に受信した状況を表示しています。再試行します。',
    chooseTitle: 'ご利用方法は？',
    chooseLead: '本日のご利用方法をお選びください。',
    modeTable: '店内で食事',
    modeTableDesc: 'テーブルの順番待ちに登録します。',
    modePickup: 'テイクアウト注文',
    modePickupDesc: 'レジで注文するために並びます。',
    pkJoinTitle: '注文の順番待ち',
    pkJoinLead: 'ご注文の順番になりましたら、この画面とWhatsAppでお知らせします。',
    pkRefHelp: '順番をお取りしています。順番になりましたらレジでご注文いただけます。',
    pkTuTurno: '順番',
    pkWaitBadge: '待機中',
    pkEspera: (m) => `約${m}分で順番です`,
    pkReservado: '順番',
    pkSubWaiting: (t) => `受付番号 ${t} · 待機中`,
    pkDlgDesc: (t) => `受付番号${t}が取り消されます。再登録すると最後尾になります。`,
    pkReadyTitle: 'ご注文いただけます！',
    pkReadyLead: 'レジへお越しになり、順番を見せてご注文ください。',
    pkGoTo: '注文レジ',
    pkGuardamos: '順番をお取り置き中',
    pkArrived: '注文しに行く',
    pkDoneTitle: 'ご注文いただけます！',
    pkNumLabel: '受付番号',
    pkDoneLead: 'この番号をスタッフに見せてご注文ください。',
    pkAtendido: (t) => `順番 ${t} · 完了`,
    pkOrderNumLabel: '注文番号',
    pkPrepTitle: 'ご注文を承りました！',
    pkPrepLead: '受け取り待ちが始まりました。準備ができ次第、WhatsAppでお知らせします。',
    pkPrepBadge: '準備中',
    pkPrepEta: (m) => `約${m}分で完成`,
    pkReady2Title: 'お受け取りください！',
    pkReady2Lead: 'カウンターで注文番号を見せて受け取ってください。',
    pkCollect: '受け取りました',
    pkDeliveredTitle: '召し上がれ！',
    pkDeliveredLead: 'ご注文ありがとうございました。',
    menuDigital: 'デジタルメニュー',
    menuPdf: 'PDFメニュー',
    cancelar: 'キャンセル',
    verUbicacion: '地図を見る',
    pkOrderInApp: 'アプリで注文する',
    mcShowDetails: '詳細を見る',
    mcWithdrawLink: 'マーケティング同意を撤回',
    mcWithdrawTitle: '同意を撤回しますか？',
    mcWithdrawDesc: 'この番号では、OLULO と参加店舗のプロモーションやお知らせを WhatsApp・SMS で受け取らなくなります。もう一度受け取るには、行列に登録するときに改めて選んでください。行列の順番や呼び出しの通知には影響しません。',
    mcWithdrawCta: 'はい、撤回します',
    mcWithdrawDone: '完了しました。この番号に OLULO のプロモーションは今後届きません。',
    mcWithdrawError: '同意を撤回できませんでした。もう一度お試しください。',
    mcWithdrawUnavailable: '現在、撤回を処理できませんでした。hola@olulo.com.mx までメールを送るか、WhatsApp のチャットで BAJA と返信してください。',
    storeFollowLabel: 'この店舗のお知らせを受け取る',
    storeFollowOffHint: 'この店舗の販促メッセージは届きません。順番のお知らせは引き続き届きます。',
    storeFollowOnHint: 'この番号にこの店舗のクーポンや最新情報が届きます。',
    storeFollowError: '設定を保存できませんでした。もう一度お試しください。',
    storeFollowReconsent: 'この店舗のお知らせを再び受け取るには、行列に登録するときに同意を選び直してください。',
  },
  zh: {
    subWaiting: (t, n) => `号码 ${t} · ${n}人`,
    filaAbierta: '排队开放中',
    filaCerrada: '暂停排队',
    cerradaDesc: '目前暂不接受排队登记。请直接联系店员。',
    qrInactivo: '二维码无效',
    qrInactivoDesc: '此二维码已失效。请向店员索取最新的二维码。',
    joinTitle: '加入排队',
    joinLead: '随时随地等候。餐桌准备好后，我们会在此页面并通过 WhatsApp 通知你。',
    howMany: '几位用餐？',
    personas: '人数',
    telefono: '电话',
    telPh: '138 0000 0000',
    telError: '请输入有效的电话号码。',
    telHelp: '尽量填写有 WhatsApp 的号码。',
    notifyNote: '我们会通过 WhatsApp 通知你。若无法送达，将改发短信。',
    teamsInfo: (n, m) => [
      { text: `${n}桌`, bold: true },
      { text: '正在等候 · 预计 ' },
      { text: `约${m}分钟`, bold: true },
    ],
    joinCta: '加入排队',
    joining: '登记中…',
    warnMax: (max) => `每次登记最多 ${max} 人。更大的团体请直接联系店员。`,
    duplicado: '此号码已有一个进行中的排队登记。',
    errorGenerico: '操作未能完成，请重试。',
    reintentar: '重试',
    tuTurno: '你的号码',
    enEspera: '等候中',
    equipos: () => '桌在你前面',
    casiTurno: '马上轮到你了！',
    teUniste: '已加入',
    tuMesa: '你的餐桌',
    esperaAprox: (m) => `预计等候约${m}分钟`,
    reservadoPara: '预留给',
    nPersonas: (n) => `${n}人`,
    aviso: '通知',
    smsRespaldo: '短信备用',
    tip: '请保持此页面打开。临近时我们会提前几分钟提醒你前往。',
    salirFila: '退出排队',
    dlgTitle: '退出排队？',
    dlgDesc: (t, n) => `你将失去号码${t}和预留的${n}个座位。重新加入需排到队尾。`,
    seguir: '继续等候',
    siSalir: '确认退出',
    esTuTurno: '轮到你了！',
    mesaLista: '你的餐桌已准备好。请到入口向接待员报到。',
    turno: '号码',
    llamado: '已叫号',
    personasLbl: '人数',
    veA: '前往',
    recepcion: '接待处 · 入口',
    guardamos: '正在为你保留座位',
    yaLlegue: '我到了',
    noPodre: '我无法到场',
    bienvenido: '欢迎！',
    teAsignamos: (mesa, n) => [
      { text: '已为你安排' },
      { text: `${mesa}号桌`, strong: true },
      { text: `，${n}人用餐。` },
    ],
    atendido: (t) => `号码 ${t} · 已入座`,
    verMenu: '查看菜单',
    volverInicio: '返回首页',
    salisteFila: '你已退出排队',
    liberamos: '已释放你的座位。你可以随时重新加入。',
    volverUnirme: '重新加入排队',
    idioma: '语言',
    gpsNote: '确认时将验证你的位置。',
    gpsChecking: '正在验证你的位置…',
    gpsFar: '你离餐厅太远。请靠近后再确认到达。',
    gpsUnavailable: '无法验证你的位置。请到入口向接待员报到。',
    cargando: '加载中…',
    partyReadError: '未能加载您的排队状态。',
    partyReadStale: '更新失败，现显示上次收到的状态。我们会再次尝试。',
    chooseTitle: '您想怎么用餐？',
    chooseLead: '选择今天的用餐方式。',
    modeTable: '堂食',
    modeTableDesc: '加入餐桌排队。',
    modePickup: '打包点单',
    modePickupDesc: '排队到柜台点单。',
    pkJoinTitle: '点单排队',
    pkJoinLead: '轮到你点单时，我们会在此页面并通过 WhatsApp 通知你。',
    pkRefHelp: '我们为你保留位置。轮到你时可到柜台点单。',
    pkTuTurno: '你的号码',
    pkWaitBadge: '排队中',
    pkEspera: (m) => `约${m}分钟后轮到你`,
    pkReservado: '号码',
    pkSubWaiting: (t) => `号码 ${t} · 排队中`,
    pkDlgDesc: (t) => `你将失去号码${t}。重新加入需排到队尾。`,
    pkReadyTitle: '现在可以点单了！',
    pkReadyLead: '请到柜台出示你的号码进行点单。',
    pkGoTo: '点单柜台',
    pkGuardamos: '正在为你保留号码',
    pkArrived: '去点单',
    pkDoneTitle: '可以点单了！',
    pkNumLabel: '你的号码',
    pkDoneLead: '向服务员出示此号码即可点单。',
    pkAtendido: (t) => `号码 ${t} · 完成`,
    pkOrderNumLabel: '订单号',
    pkPrepTitle: '已下单！',
    pkPrepLead: '取餐等待已开始。备好后我们会通过 WhatsApp 通知你。',
    pkPrepBadge: '备餐中',
    pkPrepEta: (m) => `约${m}分钟后备好`,
    pkReady2Title: '可以取餐了！',
    pkReady2Lead: '请到柜台出示订单号领取。',
    pkCollect: '已取餐',
    pkDeliveredTitle: '请慢用！',
    pkDeliveredLead: '感谢下单。',
    menuDigital: '电子菜单',
    menuPdf: 'PDF 菜单',
    cancelar: '取消',
    verUbicacion: '查看位置',
    pkOrderInApp: '在应用中下单',
    mcShowDetails: '查看详情',
    mcWithdrawLink: '撤回营销同意',
    mcWithdrawTitle: '要撤回同意吗？',
    mcWithdrawDesc: '此号码将不再通过 WhatsApp 和短信接收 OLULO 及其合作商家的促销与资讯。下次登记排队时可以重新选择接收。不会影响你的排队位置或叫号通知。',
    mcWithdrawCta: '是的，撤回',
    mcWithdrawDone: '已完成。此号码不会再收到 OLULO 的促销信息。',
    mcWithdrawError: '未能撤回你的同意，请再试一次。',
    mcWithdrawUnavailable: '目前无法处理你的撤回请求。请发邮件至 hola@olulo.com.mx，或在 WhatsApp 聊天中回复 BAJA。',
    storeFollowLabel: '接收该店铺的优惠信息',
    storeFollowOffHint: '你将不会收到该店铺的促销信息。排队叫号通知仍会照常发送。',
    storeFollowOnHint: '你将在这个号码收到该店铺的优惠券和最新消息。',
    storeFollowError: '未能保存你的偏好设置，请重试。',
    storeFollowReconsent: '如需重新接收该店铺的信息，请在下次排队登记时再次勾选同意。',
  },
  pt: {
    subWaiting: (t, n) => `Senha ${t} · ${n} pessoas`,
    filaAbierta: 'Fila aberta',
    filaCerrada: 'Fila fechada',
    cerradaDesc: 'No momento não estamos aceitando cadastros. Fale diretamente com o anfitrião.',
    qrInactivo: 'QR code inválido',
    qrInactivoDesc: 'Este código não está mais ativo. Peça ao anfitrião o código atual.',
    joinTitle: 'Entre na fila',
    joinLead: 'Espere de onde quiser. Avisamos aqui e pelo WhatsApp quando sua mesa estiver pronta.',
    howMany: 'Quantas pessoas?',
    personas: 'Pessoas',
    telefono: 'Telefone',
    telPh: '11 00000 0000',
    telError: 'Digite um número de telefone válido.',
    telHelp: 'De preferência, um número com WhatsApp.',
    notifyNote: 'Avisaremos pelo WhatsApp. Se não for entregue, enviamos um SMS.',
    teamsInfo: (n, m) => [
      { text: `${n} grupos`, bold: true },
      { text: ' na fila · aprox. ' },
      { text: `${m} min`, bold: true },
    ],
    joinCta: 'Entrar na fila',
    joining: 'Cadastrando…',
    warnMax: (max) => `O máximo por registro é de ${max} pessoas. Para grupos maiores, fale com o anfitrião.`,
    duplicado: 'Você já tem um cadastro ativo com este telefone.',
    errorGenerico: 'Não foi possível concluir. Tente novamente.',
    reintentar: 'Tentar de novo',
    tuTurno: 'Sua senha',
    enEspera: 'Aguardando',
    equipos: (n) => (n === 1 ? 'grupo na sua frente' : 'grupos na sua frente'),
    casiTurno: 'Quase sua vez!',
    teUniste: 'Você entrou',
    tuMesa: 'Sua mesa',
    esperaAprox: (m) => `Espera aprox. ${m} min`,
    reservadoPara: 'Reservado para',
    nPersonas: (n) => `${n} pessoas`,
    aviso: 'Avisos',
    smsRespaldo: 'SMS de reserva',
    tip: 'Mantenha esta tela à mão. Avisamos alguns minutos antes para você se aproximar.',
    salirFila: 'Sair da fila',
    dlgTitle: 'Sair da fila?',
    dlgDesc: (t, n) => `Você perderá a senha ${t} e ${n} lugares reservados. Terá que se cadastrar de novo no fim da fila.`,
    seguir: 'Continuar esperando',
    siSalir: 'Sim, sair',
    esTuTurno: 'É a sua vez!',
    mesaLista: 'Sua mesa está pronta. Procure o anfitrião na entrada.',
    turno: 'Senha',
    llamado: 'Chamado',
    personasLbl: 'Pessoas',
    veA: 'Vá para',
    recepcion: 'Recepção · entrada',
    guardamos: 'Estamos guardando seu lugar',
    yaLlegue: 'Já cheguei',
    noPodre: 'Não vou conseguir',
    bienvenido: 'Bem-vindo!',
    teAsignamos: (mesa, n) => [
      { text: 'Reservamos a ' },
      { text: `Mesa ${mesa}`, strong: true },
      { text: ` para ${n} pessoas.` },
    ],
    atendido: (t) => `Senha ${t} · atendido`,
    verMenu: 'Ver o cardápio',
    volverInicio: 'Voltar ao início',
    salisteFila: 'Você saiu da fila',
    liberamos: 'Liberamos seu lugar. Você pode voltar quando quiser.',
    volverUnirme: 'Entrar na fila de novo',
    idioma: 'Idioma',
    gpsNote: 'Vamos verificar sua localização ao confirmar.',
    gpsChecking: 'Verificando sua localização…',
    gpsFar: 'Você está muito longe do restaurante. Aproxime-se para confirmar sua chegada.',
    gpsUnavailable: 'Não conseguimos verificar sua localização. Procure o anfitrião na entrada.',
    cargando: 'Carregando…',
    partyReadError: 'Não foi possível carregar seu estado na fila.',
    partyReadStale: 'Não foi possível atualizar. Mostramos o último estado recebido e tentaremos novamente.',
    chooseTitle: 'Como você prefere?',
    chooseLead: 'Escolha como quer sua experiência hoje.',
    modeTable: 'Comer no local',
    modeTableDesc: 'Entre na fila para uma mesa.',
    modePickup: 'Pedir para levar',
    modePickupDesc: 'Faça fila para pedir no balcão.',
    pkJoinTitle: 'Fila para pedir',
    pkJoinLead: 'Avisamos aqui e pelo WhatsApp quando for sua vez de pedir.',
    pkRefHelp: 'Estamos guardando seu lugar. Na sua vez, você pede no balcão.',
    pkTuTurno: 'Sua vez',
    pkWaitBadge: 'Na fila',
    pkEspera: (m) => `Sua vez em aprox. ${m} min`,
    pkReservado: 'Senha',
    pkSubWaiting: (t) => `Senha ${t} · na fila`,
    pkDlgDesc: (t) => `Você perderá a senha ${t}. Terá que se cadastrar de novo no fim da fila.`,
    pkReadyTitle: 'Já pode pedir!',
    pkReadyLead: 'Vá ao balcão e mostre sua senha para fazer o pedido.',
    pkGoTo: 'Balcão de pedidos',
    pkGuardamos: 'Estamos guardando sua vez',
    pkArrived: 'Vou pedir',
    pkDoneTitle: 'Pronto para pedir!',
    pkNumLabel: 'Seu número de senha',
    pkDoneLead: 'Mostre este número ao atendente para fazer seu pedido.',
    pkAtendido: (t) => `Senha ${t} · atendido`,
    pkOrderNumLabel: 'Número do pedido',
    pkPrepTitle: 'Pedido registrado!',
    pkPrepLead: 'A espera de retirada começou. Avisaremos pelo WhatsApp quando estiver pronto.',
    pkPrepBadge: 'Preparando',
    pkPrepEta: (m) => `Pronto em aprox. ${m} min`,
    pkReady2Title: 'Pronto para retirar!',
    pkReady2Lead: 'Retire no balcão e mostre seu número.',
    pkCollect: 'Já retirei',
    pkDeliveredTitle: 'Bom apetite!',
    pkDeliveredLead: 'Obrigado pelo pedido.',
    menuDigital: 'Cardápio digital',
    menuPdf: 'Cardápio em PDF',
    cancelar: 'Cancelar',
    verUbicacion: 'Ver localização',
    pkOrderInApp: 'Pedir no aplicativo',
    mcShowDetails: 'Ver detalhes',
    mcWithdrawLink: 'Retirar consentimento de marketing',
    mcWithdrawTitle: 'Retirar seu consentimento?',
    mcWithdrawDesc: 'Você deixará de receber promoções e novidades da OLULO e dos estabelecimentos participantes por WhatsApp e SMS neste número. Você pode aceitá-las de novo ao entrar em uma fila. Isso não afeta seu lugar na fila nem os avisos da sua vez.',
    mcWithdrawCta: 'Sim, retirar',
    mcWithdrawDone: 'Pronto. Você não receberá mais promoções da OLULO neste número.',
    mcWithdrawError: 'Não conseguimos retirar seu consentimento. Tente novamente.',
    mcWithdrawUnavailable: 'Não conseguimos processar o cancelamento agora. Escreva para hola@olulo.com.mx ou responda BAJA no chat do WhatsApp.',
    storeFollowLabel: 'Receber promoções deste estabelecimento',
    storeFollowOffHint: 'Você não vai receber promoções deste estabelecimento. Os avisos da sua vez continuam chegando.',
    storeFollowOnHint: 'Você vai receber cupons e novidades deste estabelecimento neste número.',
    storeFollowError: 'Não conseguimos salvar sua preferência. Tente de novo.',
    storeFollowReconsent: 'Para voltar a receber promoções deste estabelecimento, aceite o consentimento ao entrar na fila.',
  },
  fr: {
    subWaiting: (t, n) => `Ticket ${t} · ${n} personnes`,
    filaAbierta: 'File ouverte',
    filaCerrada: 'File fermée',
    cerradaDesc: "Nous n'acceptons pas d'inscriptions pour le moment. Adressez-vous à l'accueil.",
    qrInactivo: 'QR code non valide',
    qrInactivoDesc: "Ce code n'est plus actif. Demandez le code actuel à l'accueil.",
    joinTitle: 'Rejoindre la file',
    joinLead: 'Patientez où vous voulez. Nous vous prévenons ici et sur WhatsApp quand votre table est prête.',
    howMany: 'Combien de personnes ?',
    personas: 'Personnes',
    telefono: 'Téléphone',
    telPh: '06 00 00 00 00',
    telError: 'Saisissez un numéro de téléphone valide.',
    telHelp: 'De préférence, un numéro avec WhatsApp.',
    notifyNote: "Nous vous préviendrons sur WhatsApp. En cas d'échec, par SMS.",
    teamsInfo: (n, m) => [
      { text: `${n} groupes`, bold: true },
      { text: ' en attente · env. ' },
      { text: `${m} min`, bold: true },
    ],
    joinCta: 'Rejoindre la file',
    joining: 'Inscription…',
    warnMax: (max) => `Le maximum par inscription est de ${max} personnes. Pour les groupes plus grands, adressez-vous à l'accueil.`,
    duplicado: 'Vous avez déjà une inscription active avec ce numéro.',
    errorGenerico: "Action impossible. Veuillez réessayer.",
    reintentar: 'Réessayer',
    tuTurno: 'Votre ticket',
    enEspera: 'En attente',
    equipos: (n) => (n === 1 ? 'groupe devant vous' : 'groupes devant vous'),
    casiTurno: 'Bientôt votre tour !',
    teUniste: 'Vous avez rejoint',
    tuMesa: 'Votre table',
    esperaAprox: (m) => `Attente env. ${m} min`,
    reservadoPara: 'Réservé pour',
    nPersonas: (n) => `${n} personnes`,
    aviso: 'Alertes',
    smsRespaldo: 'SMS de secours',
    tip: 'Gardez cet écran à portée. Nous vous alertons quelques minutes avant pour vous rapprocher.',
    salirFila: 'Quitter la file',
    dlgTitle: 'Quitter la file ?',
    dlgDesc: (t, n) => `Vous perdrez le ticket ${t} et ${n} places réservées. Il faudra vous réinscrire en fin de file.`,
    seguir: "Continuer d'attendre",
    siSalir: 'Oui, quitter',
    esTuTurno: "C'est votre tour !",
    mesaLista: "Votre table est prête. Présentez-vous à l'accueil à l'entrée.",
    turno: 'Ticket',
    llamado: 'Appelé',
    personasLbl: 'Personnes',
    veA: 'Allez à',
    recepcion: 'Accueil · entrée',
    guardamos: 'Nous gardons votre place',
    yaLlegue: 'Je suis là',
    noPodre: 'Je ne peux pas venir',
    bienvenido: 'Bienvenue !',
    teAsignamos: (mesa, n) => [
      { text: 'Nous vous avons attribué la ' },
      { text: `Table ${mesa}`, strong: true },
      { text: ` pour ${n} personnes.` },
    ],
    atendido: (t) => `Ticket ${t} · installé`,
    verMenu: 'Voir le menu',
    volverInicio: "Retour à l'accueil",
    salisteFila: 'Vous avez quitté la file',
    liberamos: 'Nous avons libéré votre place. Revenez quand vous voulez.',
    volverUnirme: 'Rejoindre à nouveau',
    idioma: 'Langue',
    gpsNote: 'Nous vérifierons votre position à la confirmation.',
    gpsChecking: 'Vérification de votre position…',
    gpsFar: 'Vous êtes trop loin du restaurant. Rapprochez-vous pour confirmer votre arrivée.',
    gpsUnavailable: "Nous n'avons pas pu vérifier votre position. Présentez-vous à l'accueil.",
    cargando: 'Chargement…',
    partyReadError: 'Impossible de charger votre état dans la file.',
    partyReadStale: 'La mise à jour a échoué. Nous affichons le dernier état reçu et réessaierons.',
    chooseTitle: 'Comment préférez-vous ?',
    chooseLead: "Choisissez votre expérience aujourd'hui.",
    modeTable: 'Sur place',
    modeTableDesc: 'Rejoignez la file pour une table.',
    modePickup: 'Commander à emporter',
    modePickupDesc: 'Faites la file pour commander au comptoir.',
    pkJoinTitle: 'File de commande',
    pkJoinLead: 'Nous vous prévenons ici et sur WhatsApp quand ce sera votre tour de commander.',
    pkRefHelp: 'Nous gardons votre place. À votre tour, vous commanderez au comptoir.',
    pkTuTurno: 'Votre tour',
    pkWaitBadge: 'En file',
    pkEspera: (m) => `Votre tour dans env. ${m} min`,
    pkReservado: 'Tour',
    pkSubWaiting: (t) => `Ticket ${t} · en file`,
    pkDlgDesc: (t) => `Vous perdrez le ticket ${t}. Il faudra vous réinscrire en fin de file.`,
    pkReadyTitle: 'Vous pouvez commander !',
    pkReadyLead: 'Allez au comptoir et montrez votre tour pour commander.',
    pkGoTo: 'Comptoir de commande',
    pkGuardamos: 'Nous gardons votre tour',
    pkArrived: 'Je vais commander',
    pkDoneTitle: 'Prêt à commander !',
    pkNumLabel: 'Votre numéro',
    pkDoneLead: 'Montrez ce numéro au serveur pour passer commande.',
    pkAtendido: (t) => `Tour ${t} · terminé`,
    pkOrderNumLabel: 'Numéro de commande',
    pkPrepTitle: 'Commande enregistrée !',
    pkPrepLead: "L'attente de retrait commence. Nous vous préviendrons sur WhatsApp quand ce sera prêt.",
    pkPrepBadge: 'En préparation',
    pkPrepEta: (m) => `Prête dans env. ${m} min`,
    pkReady2Title: 'Prête à retirer !',
    pkReady2Lead: 'Récupérez-la au comptoir en montrant votre numéro.',
    pkCollect: "Je l'ai récupérée",
    pkDeliveredTitle: 'Bon appétit !',
    pkDeliveredLead: 'Merci pour votre commande.',
    menuDigital: 'Menu numérique',
    menuPdf: 'Menu PDF',
    cancelar: 'Annuler',
    verUbicacion: "Voir l'emplacement",
    pkOrderInApp: "Commander dans l'application",
    mcShowDetails: 'Voir les détails',
    mcWithdrawLink: 'Retirer le consentement marketing',
    mcWithdrawTitle: 'Retirer votre consentement ?',
    mcWithdrawDesc: 'Vous ne recevrez plus les promotions et actualités d’OLULO et de ses commerces partenaires par WhatsApp et SMS sur ce numéro. Vous pourrez les accepter à nouveau en rejoignant une file. Cela n’affecte pas votre place dans la file ni les alertes de votre tour.',
    mcWithdrawCta: 'Oui, retirer',
    mcWithdrawDone: 'C’est fait. Vous ne recevrez plus de promotions OLULO sur ce numéro.',
    mcWithdrawError: 'Nous n’avons pas pu retirer votre consentement. Réessayez.',
    mcWithdrawUnavailable: 'Nous n’avons pas pu traiter votre retrait pour le moment. Écrivez-nous à hola@olulo.com.mx ou répondez BAJA dans la discussion WhatsApp.',
    storeFollowLabel: 'Recevoir les promotions de ce commerce',
    storeFollowOffHint: 'Vous ne recevrez pas les promotions de ce commerce. Les avis concernant votre tour continueront d’arriver.',
    storeFollowOnHint: 'Vous recevrez les bons et les nouveautés de ce commerce sur ce numéro.',
    storeFollowError: 'Nous n’avons pas pu enregistrer votre préférence. Réessayez.',
    storeFollowReconsent: 'Pour recevoir à nouveau les promotions de ce commerce, acceptez le consentement lors de votre inscription dans la file.',
  },
  de: {
    subWaiting: (t, n) => `Nummer ${t} · ${n} Personen`,
    filaAbierta: 'Warteliste offen',
    filaCerrada: 'Warteliste geschlossen',
    cerradaDesc: 'Wir nehmen derzeit keine Anmeldungen an. Wenden Sie sich direkt an den Empfang.',
    qrInactivo: 'Ungültiger QR-Code',
    qrInactivoDesc: 'Dieser Code ist nicht mehr aktiv. Fragen Sie am Empfang nach dem aktuellen Code.',
    joinTitle: 'Der Warteliste beitreten',
    joinLead: 'Warten Sie von überall. Wir benachrichtigen Sie hier und über WhatsApp, sobald Ihr Tisch bereit ist.',
    howMany: 'Wie viele Personen?',
    personas: 'Personen',
    telefono: 'Telefon',
    telPh: '0151 0000000',
    telError: 'Geben Sie eine gültige Telefonnummer ein.',
    telHelp: 'Am besten eine Nummer mit WhatsApp.',
    notifyNote: 'Wir benachrichtigen Sie über WhatsApp. Falls das nicht klappt, per SMS.',
    teamsInfo: (n, m) => [
      { text: `${n} Gruppen`, bold: true },
      { text: ' warten · ca. ' },
      { text: `${m} Min.`, bold: true },
    ],
    joinCta: 'Beitreten',
    joining: 'Anmeldung…',
    warnMax: (max) => `Maximal ${max} Personen pro Anmeldung. Für größere Gruppen wenden Sie sich direkt an den Empfang.`,
    duplicado: 'Mit dieser Nummer besteht bereits eine aktive Anmeldung.',
    errorGenerico: 'Das hat nicht geklappt. Bitte erneut versuchen.',
    reintentar: 'Erneut versuchen',
    tuTurno: 'Ihre Nummer',
    enEspera: 'Wartend',
    equipos: (n) => (n === 1 ? 'Gruppe vor Ihnen' : 'Gruppen vor Ihnen'),
    casiTurno: 'Fast dran!',
    teUniste: 'Beigetreten',
    tuMesa: 'Ihr Tisch',
    esperaAprox: (m) => `Wartezeit ca. ${m} Min.`,
    reservadoPara: 'Reserviert für',
    nPersonas: (n) => `${n} Personen`,
    aviso: 'Hinweise',
    smsRespaldo: 'SMS als Ausweich',
    tip: 'Behalten Sie diesen Bildschirm im Blick. Wir melden uns wenige Minuten vorher.',
    salirFila: 'Warteliste verlassen',
    dlgTitle: 'Warteliste verlassen?',
    dlgDesc: (t, n) => `Sie verlieren Nummer ${t} und ${n} reservierte Plätze. Sie müssten sich am Ende neu anmelden.`,
    seguir: 'Weiter warten',
    siSalir: 'Ja, verlassen',
    esTuTurno: 'Sie sind dran!',
    mesaLista: 'Ihr Tisch ist bereit. Melden Sie sich beim Empfang am Eingang.',
    turno: 'Nummer',
    llamado: 'Aufgerufen',
    personasLbl: 'Personen',
    veA: 'Gehen Sie zu',
    recepcion: 'Empfang · Eingang',
    guardamos: 'Wir halten Ihren Platz frei',
    yaLlegue: 'Ich bin da',
    noPodre: 'Ich schaffe es nicht',
    bienvenido: 'Willkommen!',
    teAsignamos: (mesa, n) => [
      { text: 'Wir haben Ihnen ' },
      { text: `Tisch ${mesa}`, strong: true },
      { text: ` für ${n} Personen zugewiesen.` },
    ],
    atendido: (t) => `Nummer ${t} · platziert`,
    verMenu: 'Zur Speisekarte',
    volverInicio: 'Zurück zum Start',
    salisteFila: 'Sie haben die Warteliste verlassen',
    liberamos: 'Ihr Platz wurde freigegeben. Sie können jederzeit wieder beitreten.',
    volverUnirme: 'Erneut beitreten',
    idioma: 'Sprache',
    gpsNote: 'Wir prüfen Ihren Standort bei der Bestätigung.',
    gpsChecking: 'Standort wird geprüft…',
    gpsFar: 'Sie sind zu weit vom Restaurant entfernt. Kommen Sie näher, um Ihre Ankunft zu bestätigen.',
    gpsUnavailable: 'Wir konnten Ihren Standort nicht prüfen. Melden Sie sich beim Empfang am Eingang.',
    cargando: 'Wird geladen…',
    partyReadError: 'Dein Wartestatus konnte nicht geladen werden.',
    partyReadStale: 'Die Aktualisierung ist fehlgeschlagen. Wir zeigen den zuletzt empfangenen Status und versuchen es erneut.',
    chooseTitle: 'Wie möchten Sie?',
    chooseLead: 'Wählen Sie, wie Sie es heute möchten.',
    modeTable: 'Vor Ort essen',
    modeTableDesc: 'Reihen Sie sich für einen Tisch ein.',
    modePickup: 'Zum Mitnehmen bestellen',
    modePickupDesc: 'Stellen Sie sich zum Bestellen an der Theke an.',
    pkJoinTitle: 'Bestellschlange',
    pkJoinLead: 'Wir benachrichtigen Sie hier und über WhatsApp, wenn Sie bestellen können.',
    pkRefHelp: 'Wir halten Ihren Platz. Wenn Sie dran sind, bestellen Sie an der Theke.',
    pkTuTurno: 'Sie sind dran',
    pkWaitBadge: 'In der Schlange',
    pkEspera: (m) => `Dran in ca. ${m} Min.`,
    pkReservado: 'Nummer',
    pkSubWaiting: (t) => `Nummer ${t} · in der Schlange`,
    pkDlgDesc: (t) => `Sie verlieren Nummer ${t}. Sie müssten sich am Ende der Schlange neu anmelden.`,
    pkReadyTitle: 'Sie können bestellen!',
    pkReadyLead: 'Gehen Sie zur Theke und zeigen Sie Ihre Nummer zum Bestellen.',
    pkGoTo: 'Bestelltheke',
    pkGuardamos: 'Wir halten Ihre Nummer',
    pkArrived: 'Ich bestelle',
    pkDoneTitle: 'Bereit zum Bestellen!',
    pkNumLabel: 'Ihre Nummer',
    pkDoneLead: 'Zeigen Sie diese Nummer dem Personal, um zu bestellen.',
    pkAtendido: (t) => `Nummer ${t} · fertig`,
    pkOrderNumLabel: 'Bestellnummer',
    pkPrepTitle: 'Bestellung aufgegeben!',
    pkPrepLead: 'Die Abholwartezeit beginnt. Wir melden uns über WhatsApp, sobald es fertig ist.',
    pkPrepBadge: 'In Zubereitung',
    pkPrepEta: (m) => `Fertig in ca. ${m} Min.`,
    pkReady2Title: 'Abholbereit!',
    pkReady2Lead: 'Holen Sie es an der Theke ab und zeigen Sie Ihre Nummer.',
    pkCollect: 'Abgeholt',
    pkDeliveredTitle: 'Guten Appetit!',
    pkDeliveredLead: 'Danke für Ihre Bestellung.',
    menuDigital: 'Digitale Karte',
    menuPdf: 'PDF-Karte',
    cancelar: 'Abbrechen',
    verUbicacion: 'Standort ansehen',
    pkOrderInApp: 'In der App bestellen',
    mcShowDetails: 'Details anzeigen',
    mcWithdrawLink: 'Marketing-Einwilligung widerrufen',
    mcWithdrawTitle: 'Einwilligung widerrufen?',
    mcWithdrawDesc: 'Du erhältst auf dieser Nummer keine Aktionen und Neuigkeiten von OLULO und seinen teilnehmenden Betrieben mehr per WhatsApp und SMS. Du kannst sie wieder zulassen, wenn du dich für eine Warteschlange anmeldest. Das betrifft nicht deinen Platz in der Warteschlange oder die Benachrichtigung, wenn du aufgerufen wirst.',
    mcWithdrawCta: 'Ja, widerrufen',
    mcWithdrawDone: 'Fertig. Du erhältst auf dieser Nummer keine OLULO-Aktionen mehr.',
    mcWithdrawError: 'Wir konnten deine Einwilligung nicht widerrufen. Bitte versuche es erneut.',
    mcWithdrawUnavailable: 'Wir konnten deinen Widerruf gerade nicht verarbeiten. Schreib uns an hola@olulo.com.mx oder antworte BAJA im WhatsApp-Chat.',
    storeFollowLabel: 'Angebote dieses Lokals erhalten',
    storeFollowOffHint: 'Du erhältst keine Angebote dieses Lokals. Die Benachrichtigungen zu deiner Wartenummer kommen weiterhin.',
    storeFollowOnHint: 'Du erhältst Gutscheine und Neuigkeiten dieses Lokals an diese Nummer.',
    storeFollowError: 'Wir konnten deine Einstellung nicht speichern. Versuch es noch einmal.',
    storeFollowReconsent: 'Um wieder Angebote dieses Lokals zu erhalten, stimme bei der Anmeldung in der Warteschlange erneut zu.',
  },
  it: {
    subWaiting: (t, n) => `Numero ${t} · ${n} persone`,
    filaAbierta: 'Lista aperta',
    filaCerrada: 'Lista chiusa',
    cerradaDesc: 'Al momento non accettiamo registrazioni. Rivolgiti direttamente all\'accoglienza.',
    qrInactivo: 'Codice QR non valido',
    qrInactivoDesc: 'Questo codice non è più attivo. Chiedi all\'accoglienza quello aggiornato.',
    joinTitle: 'Mettiti in fila',
    joinLead: 'Aspetta da dove vuoi. Ti avvisiamo qui e su WhatsApp quando il tavolo è pronto.',
    howMany: 'Quante persone?',
    personas: 'Persone',
    telefono: 'Telefono',
    telPh: '320 000 0000',
    telError: 'Inserisci un numero di telefono valido.',
    telHelp: 'Preferibilmente un numero con WhatsApp.',
    notifyNote: 'Ti avviseremo su WhatsApp. Se non arriva, ti mandiamo un SMS.',
    teamsInfo: (n, m) => [
      { text: `${n} gruppi`, bold: true },
      { text: ' in attesa · circa ' },
      { text: `${m} min`, bold: true },
    ],
    joinCta: 'Mettiti in fila',
    joining: 'Registrazione…',
    warnMax: (max) => `Il massimo per registrazione è di ${max} persone. Per gruppi più grandi, rivolgiti all'accoglienza.`,
    duplicado: 'Hai già una registrazione attiva con questo numero.',
    errorGenerico: 'Non è stato possibile completare. Riprova.',
    reintentar: 'Riprova',
    tuTurno: 'Il tuo numero',
    enEspera: 'In attesa',
    equipos: (n) => (n === 1 ? 'gruppo davanti a te' : 'gruppi davanti a te'),
    casiTurno: 'Quasi il tuo turno!',
    teUniste: 'Sei in fila',
    tuMesa: 'Il tuo tavolo',
    esperaAprox: (m) => `Attesa circa ${m} min`,
    reservadoPara: 'Riservato per',
    nPersonas: (n) => `${n} persone`,
    aviso: 'Avvisi',
    smsRespaldo: 'SMS di riserva',
    tip: 'Tieni questa schermata a portata. Ti avvisiamo qualche minuto prima così ti avvicini.',
    salirFila: 'Esci dalla fila',
    dlgTitle: 'Uscire dalla fila?',
    dlgDesc: (t, n) => `Perderai il numero ${t} e ${n} posti riservati. Dovrai registrarti di nuovo in fondo alla fila.`,
    seguir: 'Continua ad aspettare',
    siSalir: 'Sì, esci',
    esTuTurno: 'È il tuo turno!',
    mesaLista: 'Il tuo tavolo è pronto. Presentati all\'accoglienza all\'ingresso.',
    turno: 'Numero',
    llamado: 'Chiamato',
    personasLbl: 'Persone',
    veA: 'Vai a',
    recepcion: 'Accoglienza · ingresso',
    guardamos: 'Ti stiamo tenendo il posto',
    yaLlegue: 'Sono qui',
    noPodre: 'Non riesco a venire',
    bienvenido: 'Benvenuto!',
    teAsignamos: (mesa, n) => [
      { text: 'Ti abbiamo assegnato il ' },
      { text: `Tavolo ${mesa}`, strong: true },
      { text: ` per ${n} persone.` },
    ],
    atendido: (t) => `Numero ${t} · accomodato`,
    verMenu: 'Vedi il menù',
    volverInicio: "Torna all'inizio",
    salisteFila: 'Sei uscito dalla fila',
    liberamos: 'Abbiamo liberato il tuo posto. Puoi rientrare quando vuoi.',
    volverUnirme: 'Rientra in fila',
    idioma: 'Lingua',
    gpsNote: 'Verificheremo la tua posizione alla conferma.',
    gpsChecking: 'Verifica della posizione…',
    gpsFar: 'Sei troppo lontano dal ristorante. Avvicinati per confermare il tuo arrivo.',
    gpsUnavailable: 'Non abbiamo potuto verificare la tua posizione. Presentati all\'accoglienza.',
    cargando: 'Caricamento…',
    partyReadError: 'Non è stato possibile caricare il tuo stato in coda.',
    partyReadStale: 'Aggiornamento non riuscito. Mostriamo l’ultimo stato ricevuto e riproveremo.',
    chooseTitle: 'Come preferisci?',
    chooseLead: 'Scegli come vuoi la tua esperienza oggi.',
    modeTable: 'Mangia qui',
    modeTableDesc: 'Mettiti in fila per un tavolo.',
    modePickup: 'Ordina da asporto',
    modePickupDesc: 'Mettiti in fila per ordinare alla cassa.',
    pkJoinTitle: 'Fila per ordinare',
    pkJoinLead: 'Ti avvisiamo qui e su WhatsApp quando è il tuo turno di ordinare.',
    pkRefHelp: 'Ti teniamo il posto. Al tuo turno ordini alla cassa.',
    pkTuTurno: 'Il tuo turno',
    pkWaitBadge: 'In fila',
    pkEspera: (m) => `Il tuo turno tra circa ${m} min`,
    pkReservado: 'Turno',
    pkSubWaiting: (t) => `Numero ${t} · in fila`,
    pkDlgDesc: (t) => `Perderai il numero ${t}. Dovrai registrarti di nuovo in fondo alla fila.`,
    pkReadyTitle: 'Puoi ordinare!',
    pkReadyLead: 'Vai alla cassa e mostra il tuo turno per ordinare.',
    pkGoTo: 'Cassa ordini',
    pkGuardamos: 'Ti teniamo il turno',
    pkArrived: 'Vado a ordinare',
    pkDoneTitle: 'Pronto per ordinare!',
    pkNumLabel: 'Il tuo numero',
    pkDoneLead: 'Mostra questo numero al cameriere per ordinare.',
    pkAtendido: (t) => `Turno ${t} · servito`,
    pkOrderNumLabel: "Numero d'ordine",
    pkPrepTitle: 'Ordine registrato!',
    pkPrepLead: "Inizia l'attesa per il ritiro. Ti avviseremo su WhatsApp quando sarà pronto.",
    pkPrepBadge: 'In preparazione',
    pkPrepEta: (m) => `Pronto tra circa ${m} min`,
    pkReady2Title: 'Pronto da ritirare!',
    pkReady2Lead: 'Ritira al banco mostrando il tuo numero.',
    pkCollect: 'Ritirato',
    pkDeliveredTitle: 'Buon appetito!',
    pkDeliveredLead: 'Grazie per il tuo ordine.',
    menuDigital: 'Menù digitale',
    menuPdf: 'Menù PDF',
    cancelar: 'Annulla',
    verUbicacion: 'Vedi posizione',
    pkOrderInApp: "Ordina nell'app",
    mcShowDetails: 'Mostra i dettagli',
    mcWithdrawLink: 'Revoca il consenso marketing',
    mcWithdrawTitle: 'Revocare il consenso?',
    mcWithdrawDesc: 'Su questo numero non riceverai più promozioni e novità di OLULO e dei suoi locali partner via WhatsApp e SMS. Puoi accettarle di nuovo quando ti metti in fila. Questo non influisce sul tuo posto in fila né sugli avvisi del tuo turno.',
    mcWithdrawCta: 'Sì, revoca',
    mcWithdrawDone: 'Fatto. Non riceverai più promozioni OLULO su questo numero.',
    mcWithdrawError: 'Non siamo riusciti a revocare il consenso. Riprova.',
    mcWithdrawUnavailable: 'Non siamo riusciti a elaborare la revoca in questo momento. Scrivici a hola@olulo.com.mx oppure rispondi BAJA nella chat di WhatsApp.',
    storeFollowLabel: 'Ricevere le promozioni di questo locale',
    storeFollowOffHint: 'Non riceverai le promozioni di questo locale. Continuerai a ricevere gli avvisi sul tuo turno.',
    storeFollowOnHint: 'Riceverai coupon e novità di questo locale su questo numero.',
    storeFollowError: 'Non siamo riusciti a salvare la tua preferenza. Riprova.',
    storeFollowReconsent: 'Per ricevere di nuovo le promozioni di questo locale, accetta il consenso quando ti metti in fila.',
  },
  ru: {
    subWaiting: (t, n) => `Номер ${t} · ${n} чел.`,
    filaAbierta: 'Запись открыта',
    filaCerrada: 'Запись закрыта',
    cerradaDesc: 'Сейчас мы не принимаем записи. Обратитесь напрямую к администратору.',
    qrInactivo: 'Недействительный QR-код',
    qrInactivoDesc: 'Этот код больше не активен. Попросите у администратора актуальный.',
    joinTitle: 'Встать в очередь',
    joinLead: 'Ждите откуда угодно. Мы сообщим здесь и в WhatsApp, когда столик будет готов.',
    howMany: 'Сколько человек?',
    personas: 'Гостей',
    telefono: 'Телефон',
    telPh: '999 000 0000',
    telError: 'Введите действительный номер телефона.',
    telHelp: 'Желательно номер с WhatsApp.',
    notifyNote: 'Уведомим вас в WhatsApp. Если не доставлено — пришлём SMS.',
    teamsInfo: (n, m) => [
      { text: `${n} групп`, bold: true },
      { text: ' в очереди · около ' },
      { text: `${m} мин`, bold: true },
    ],
    joinCta: 'Встать в очередь',
    joining: 'Записываем…',
    warnMax: (max) => `Максимум за одну запись — ${max} чел. Для больших групп обратитесь к администратору.`,
    duplicado: 'На этот номер уже есть активная запись.',
    errorGenerico: 'Не удалось выполнить. Попробуйте ещё раз.',
    reintentar: 'Повторить',
    tuTurno: 'Ваш номер',
    enEspera: 'Ожидание',
    equipos: () => 'групп перед вами',
    casiTurno: 'Почти ваша очередь!',
    teUniste: 'Вы в очереди',
    tuMesa: 'Ваш столик',
    esperaAprox: (m) => `Ожидание около ${m} мин`,
    reservadoPara: 'Забронировано на',
    nPersonas: (n) => `${n} чел.`,
    aviso: 'Оповещения',
    smsRespaldo: 'SMS запасной',
    tip: 'Держите этот экран под рукой. Мы предупредим за несколько минут, чтобы вы подошли.',
    salirFila: 'Выйти из очереди',
    dlgTitle: 'Выйти из очереди?',
    dlgDesc: (t, n) => `Вы потеряете номер ${t} и ${n} забронированных мест. Придётся записаться заново в конец очереди.`,
    seguir: 'Продолжить ждать',
    siSalir: 'Да, выйти',
    esTuTurno: 'Ваша очередь!',
    mesaLista: 'Ваш столик готов. Подойдите к администратору у входа.',
    turno: 'Номер',
    llamado: 'Вызван',
    personasLbl: 'Гостей',
    veA: 'Пройдите к',
    recepcion: 'Ресепшн · вход',
    guardamos: 'Держим ваше место',
    yaLlegue: 'Я здесь',
    noPodre: 'Не смогу прийти',
    bienvenido: 'Добро пожаловать!',
    teAsignamos: (mesa, n) => [
      { text: 'Мы назначили вам ' },
      { text: `столик ${mesa}`, strong: true },
      { text: ` на ${n} чел.` },
    ],
    atendido: (t) => `Номер ${t} · посажен`,
    verMenu: 'Посмотреть меню',
    volverInicio: 'В начало',
    salisteFila: 'Вы вышли из очереди',
    liberamos: 'Мы освободили ваше место. Вы можете вернуться в любое время.',
    volverUnirme: 'Встать в очередь снова',
    idioma: 'Язык',
    gpsNote: 'Мы проверим ваше местоположение при подтверждении.',
    gpsChecking: 'Проверяем местоположение…',
    gpsFar: 'Вы слишком далеко от ресторана. Подойдите ближе, чтобы подтвердить прибытие.',
    gpsUnavailable: 'Не удалось проверить местоположение. Подойдите к администратору у входа.',
    cargando: 'Загрузка…',
    partyReadError: 'Не удалось загрузить ваш статус в очереди.',
    partyReadStale: 'Не удалось обновить данные. Показываем последний полученный статус и попробуем снова.',
    chooseTitle: 'Как вам удобнее?',
    chooseLead: 'Выберите, как хотите сегодня.',
    modeTable: 'В зале',
    modeTableDesc: 'Встаньте в очередь за столик.',
    modePickup: 'Заказ на вынос',
    modePickupDesc: 'Встаньте в очередь, чтобы сделать заказ на кассе.',
    pkJoinTitle: 'Очередь на заказ',
    pkJoinLead: 'Сообщим здесь и в WhatsApp, когда придёт ваша очередь заказать.',
    pkRefHelp: 'Мы держим ваше место. В свою очередь вы закажете на кассе.',
    pkTuTurno: 'Ваша очередь',
    pkWaitBadge: 'В очереди',
    pkEspera: (m) => `Ваша очередь примерно через ${m} мин`,
    pkReservado: 'Номер',
    pkSubWaiting: (t) => `Номер ${t} · в очереди`,
    pkDlgDesc: (t) => `Вы потеряете номер ${t}. Придётся записаться заново в конец очереди.`,
    pkReadyTitle: 'Можно заказывать!',
    pkReadyLead: 'Подойдите к кассе и покажите свой номер, чтобы сделать заказ.',
    pkGoTo: 'Касса заказов',
    pkGuardamos: 'Держим вашу очередь',
    pkArrived: 'Иду заказывать',
    pkDoneTitle: 'Готово к заказу!',
    pkNumLabel: 'Ваш номер',
    pkDoneLead: 'Покажите этот номер официанту, чтобы сделать заказ.',
    pkAtendido: (t) => `Очередь ${t} · готово`,
    pkOrderNumLabel: 'Номер заказа',
    pkPrepTitle: 'Заказ оформлен!',
    pkPrepLead: 'Началось ожидание выдачи. Сообщим в WhatsApp, когда будет готово.',
    pkPrepBadge: 'Готовится',
    pkPrepEta: (m) => `Готов примерно через ${m} мин`,
    pkReady2Title: 'Готов к выдаче!',
    pkReady2Lead: 'Заберите на стойке, показав свой номер.',
    pkCollect: 'Забрал(а)',
    pkDeliveredTitle: 'Приятного аппетита!',
    pkDeliveredLead: 'Спасибо за заказ.',
    menuDigital: 'Электронное меню',
    menuPdf: 'Меню в PDF',
    cancelar: 'Отмена',
    verUbicacion: 'Посмотреть местоположение',
    pkOrderInApp: 'Заказать в приложении',
    mcShowDetails: 'Показать подробности',
    mcWithdrawLink: 'Отозвать согласие на маркетинг',
    mcWithdrawTitle: 'Отозвать согласие?',
    mcWithdrawDesc: 'На этот номер вы больше не будете получать акции и новости OLULO и её заведений-партнёров по WhatsApp и SMS. Согласие можно дать снова при записи в очередь. Это не повлияет на ваше место в очереди или уведомления о вызове.',
    mcWithdrawCta: 'Да, отозвать',
    mcWithdrawDone: 'Готово. На этот номер акции OLULO больше не придут.',
    mcWithdrawError: 'Не удалось отозвать согласие. Попробуйте ещё раз.',
    mcWithdrawUnavailable: 'Сейчас не удалось обработать отзыв согласия. Напишите нам на hola@olulo.com.mx или отправьте BAJA в чат WhatsApp.',
    storeFollowLabel: 'Получать акции этого заведения',
    storeFollowOffHint: 'Вы не будете получать акции этого заведения. Уведомления об очереди продолжат приходить.',
    storeFollowOnHint: 'На этот номер будут приходить купоны и новости этого заведения.',
    storeFollowError: 'Не удалось сохранить настройку. Попробуйте ещё раз.',
    storeFollowReconsent: 'Чтобы снова получать акции этого заведения, отметьте согласие при записи в очередь.',
  },
  hi: {
    subWaiting: (t, n) => `टोकन ${t} · ${n} लोग`,
    filaAbierta: 'कतार खुली है',
    filaCerrada: 'कतार बंद है',
    cerradaDesc: 'अभी हम पंजीकरण नहीं ले रहे हैं। कृपया सीधे स्टाफ से संपर्क करें।',
    qrInactivo: 'अमान्य QR कोड',
    qrInactivoDesc: 'यह कोड अब सक्रिय नहीं है। स्टाफ से मौजूदा कोड मांगें।',
    joinTitle: 'कतार में शामिल हों',
    joinLead: 'कहीं से भी इंतज़ार करें। मेज़ तैयार होने पर हम यहाँ और WhatsApp पर बताएंगे।',
    howMany: 'कितने लोग?',
    personas: 'लोग',
    telefono: 'फ़ोन',
    telPh: '98000 00000',
    telError: 'कृपया मान्य फ़ोन नंबर डालें।',
    telHelp: 'यथासंभव WhatsApp वाला नंबर दें।',
    notifyNote: 'हम आपको WhatsApp पर सूचित करेंगे। न पहुँचने पर SMS भेजेंगे।',
    teamsInfo: (n, m) => [
      { text: `${n} समूह`, bold: true },
      { text: ' कतार में · लगभग ' },
      { text: `${m} मिनट`, bold: true },
    ],
    joinCta: 'कतार में शामिल हों',
    joining: 'पंजीकरण हो रहा है…',
    warnMax: (max) => `प्रति पंजीकरण अधिकतम ${max} लोग। बड़े समूहों के लिए सीधे स्टाफ से संपर्क करें।`,
    duplicado: 'इस नंबर से पहले से एक सक्रिय पंजीकरण है।',
    errorGenerico: 'काम पूरा नहीं हो सका। फिर कोशिश करें।',
    reintentar: 'फिर कोशिश करें',
    tuTurno: 'आपका टोकन',
    enEspera: 'प्रतीक्षा में',
    equipos: () => 'समूह आपसे आगे हैं',
    casiTurno: 'बस आपकी बारी आने वाली है!',
    teUniste: 'शामिल हो गए',
    tuMesa: 'आपकी मेज़',
    esperaAprox: (m) => `लगभग ${m} मिनट प्रतीक्षा`,
    reservadoPara: 'इनके लिए आरक्षित',
    nPersonas: (n) => `${n} लोग`,
    aviso: 'सूचनाएँ',
    smsRespaldo: 'SMS बैकअप',
    tip: 'इस स्क्रीन को खुला रखें। कुछ मिनट पहले हम आपको सूचित करेंगे ताकि आप पहुँच सकें।',
    salirFila: 'कतार से बाहर निकलें',
    dlgTitle: 'कतार से बाहर निकलें?',
    dlgDesc: (t, n) => `आप टोकन ${t} और ${n} आरक्षित सीटें खो देंगे। दोबारा जुड़ने पर कतार के अंत में लगना होगा।`,
    seguir: 'इंतज़ार जारी रखें',
    siSalir: 'हाँ, बाहर निकलें',
    esTuTurno: 'आपकी बारी है!',
    mesaLista: 'आपकी मेज़ तैयार है। प्रवेश द्वार पर होस्ट से मिलें।',
    turno: 'टोकन',
    llamado: 'बुलाया गया',
    personasLbl: 'लोग',
    veA: 'यहाँ जाएँ',
    recepcion: 'रिसेप्शन · प्रवेश',
    guardamos: 'हम आपकी जगह रोक रहे हैं',
    yaLlegue: 'मैं आ गया',
    noPodre: 'मैं नहीं आ पाऊँगा',
    bienvenido: 'स्वागत है!',
    teAsignamos: (mesa, n) => [
      { text: `हमने आपको ${n} लोगों के लिए ` },
      { text: `मेज़ ${mesa}`, strong: true },
      { text: ' दी है।' },
    ],
    atendido: (t) => `टोकन ${t} · बैठ गए`,
    verMenu: 'मेन्यू देखें',
    volverInicio: 'शुरुआत पर लौटें',
    salisteFila: 'आप कतार से बाहर निकल गए',
    liberamos: 'हमने आपकी जगह छोड़ दी। आप जब चाहें फिर जुड़ सकते हैं।',
    volverUnirme: 'फिर से कतार में जुड़ें',
    idioma: 'भाषा',
    gpsNote: 'पुष्टि करते समय हम आपका स्थान सत्यापित करेंगे।',
    gpsChecking: 'आपका स्थान जाँचा जा रहा है…',
    gpsFar: 'आप रेस्तरां से बहुत दूर हैं। आगमन की पुष्टि के लिए पास आएँ।',
    gpsUnavailable: 'हम आपका स्थान सत्यापित नहीं कर सके। प्रवेश द्वार पर होस्ट से मिलें।',
    cargando: 'लोड हो रहा है…',
    partyReadError: 'आपकी कतार की स्थिति लोड नहीं हो सकी।',
    partyReadStale: 'स्थिति अपडेट नहीं हो सकी। आखिरी मिली स्थिति दिखा रहे हैं और फिर कोशिश करेंगे।',
    chooseTitle: 'आप कैसे चाहेंगे?',
    chooseLead: 'आज अपना अनुभव कैसे चाहते हैं, चुनें।',
    modeTable: 'यहीं बैठकर खाएँ',
    modeTableDesc: 'मेज़ के लिए कतार में लगें।',
    modePickup: 'ले जाने के लिए ऑर्डर',
    modePickupDesc: 'काउंटर पर ऑर्डर करने के लिए कतार में लगें।',
    pkJoinTitle: 'ऑर्डर कतार',
    pkJoinLead: 'आपकी ऑर्डर की बारी आने पर हम यहाँ और WhatsApp पर बताएंगे।',
    pkRefHelp: 'हम आपकी जगह रोक रहे हैं। बारी आने पर आप काउंटर पर ऑर्डर करेंगे।',
    pkTuTurno: 'आपकी बारी',
    pkWaitBadge: 'कतार में',
    pkEspera: (m) => `लगभग ${m} मिनट में आपकी बारी`,
    pkReservado: 'टोकन',
    pkSubWaiting: (t) => `टोकन ${t} · कतार में`,
    pkDlgDesc: (t) => `आप टोकन ${t} खो देंगे। दोबारा जुड़ने पर कतार के अंत में लगना होगा।`,
    pkReadyTitle: 'अब ऑर्डर करें!',
    pkReadyLead: 'काउंटर पर जाएँ और ऑर्डर के लिए अपना टोकन दिखाएँ।',
    pkGoTo: 'ऑर्डर काउंटर',
    pkGuardamos: 'हम आपकी बारी रोक रहे हैं',
    pkArrived: 'ऑर्डर करने जा रहा हूँ',
    pkDoneTitle: 'ऑर्डर के लिए तैयार!',
    pkNumLabel: 'आपका टोकन नंबर',
    pkDoneLead: 'ऑर्डर के लिए यह नंबर स्टाफ को दिखाएँ।',
    pkAtendido: (t) => `टोकन ${t} · पूरा`,
    pkOrderNumLabel: 'ऑर्डर नंबर',
    pkPrepTitle: 'ऑर्डर दर्ज हुआ!',
    pkPrepLead: 'पिकअप प्रतीक्षा शुरू हो गई। तैयार होने पर WhatsApp पर बताएंगे।',
    pkPrepBadge: 'तैयार हो रहा है',
    pkPrepEta: (m) => `लगभग ${m} मिनट में तैयार`,
    pkReady2Title: 'पिकअप के लिए तैयार!',
    pkReady2Lead: 'काउंटर पर नंबर दिखाकर लें।',
    pkCollect: 'ले लिया',
    pkDeliveredTitle: 'भोजन का आनंद लें!',
    pkDeliveredLead: 'ऑर्डर के लिए धन्यवाद।',
    menuDigital: 'डिजिटल मेन्यू',
    menuPdf: 'PDF मेन्यू',
    cancelar: 'रद्द करें',
    verUbicacion: 'स्थान देखें',
    pkOrderInApp: 'ऐप में ऑर्डर करें',
    mcShowDetails: 'विवरण देखें',
    mcWithdrawLink: 'मार्केटिंग सहमति वापस लें',
    mcWithdrawTitle: 'अपनी सहमति वापस लें?',
    mcWithdrawDesc: 'इस नंबर पर आपको OLULO और उसके सहभागी स्टोर की प्रमोशन और खबरें WhatsApp और SMS पर मिलनी बंद हो जाएँगी। दोबारा पाने के लिए कतार में शामिल होते समय फिर से चुन लें। इससे कतार में आपकी जगह या आपकी बारी की सूचना पर कोई असर नहीं पड़ता।',
    mcWithdrawCta: 'हाँ, वापस लें',
    mcWithdrawDone: 'हो गया। अब इस नंबर पर आपको OLULO की प्रमोशन नहीं मिलेंगी।',
    mcWithdrawError: 'हम आपकी सहमति वापस नहीं ले सके। कृपया फिर से कोशिश करें।',
    mcWithdrawUnavailable: 'हम अभी आपका अनुरोध प्रोसेस नहीं कर सके। हमें hola@olulo.com.mx पर लिखें या WhatsApp चैट में BAJA का जवाब भेजें।',
    storeFollowLabel: 'इस दुकान के ऑफ़र पाएं',
    storeFollowOffHint: 'आपको इस दुकान के ऑफ़र नहीं मिलेंगे। आपकी बारी की सूचनाएँ मिलती रहेंगी।',
    storeFollowOnHint: 'आपको इस नंबर पर इस दुकान के कूपन और अपडेट मिलेंगे।',
    storeFollowError: 'हम आपकी पसंद सहेज नहीं सके। कृपया दोबारा कोशिश करें।',
    storeFollowReconsent: 'इस दुकान के ऑफ़र दोबारा पाने के लिए, लाइन में दर्ज कराते समय सहमति फिर से चुनें।',
  },
  ar: {
    subWaiting: (t, n) => `رقم ${t} · ${n} أشخاص`,
    filaAbierta: 'قائمة الانتظار مفتوحة',
    filaCerrada: 'قائمة الانتظار مغلقة',
    cerradaDesc: 'لا نستقبل تسجيلات حاليًا. توجّه إلى المضيف مباشرة.',
    qrInactivo: 'رمز QR غير صالح',
    qrInactivoDesc: 'لم يعد هذا الرمز فعّالًا. اطلب من المضيف الرمز الحالي.',
    joinTitle: 'انضم إلى الطابور',
    joinLead: 'انتظر من أي مكان. سننبهك هنا وعبر WhatsApp عندما تجهز طاولتك.',
    howMany: 'كم عدد الأشخاص؟',
    personas: 'أشخاص',
    telefono: 'الهاتف',
    telPh: '050 000 0000',
    telError: 'أدخل رقم هاتف صالح.',
    telHelp: 'يُفضّل رقم مفعّل عليه WhatsApp.',
    notifyNote: 'سننبّهك عبر WhatsApp، وإن تعذّر وصوله نرسل لك SMS.',
    teamsInfo: (n, m) => [
      { text: `${n} مجموعات`, bold: true },
      { text: ' في الانتظار · حوالي ' },
      { text: `${m} دقيقة`, bold: true },
    ],
    joinCta: 'انضم إلى الطابور',
    joining: 'جارٍ التسجيل…',
    warnMax: (max) => `الحد الأقصى للتسجيل الواحد ${max} أشخاص. للمجموعات الأكبر، توجّه إلى المضيف مباشرة.`,
    duplicado: 'لديك بالفعل تسجيل نشط بهذا الرقم.',
    errorGenerico: 'تعذّر إتمام العملية. حاول مرة أخرى.',
    reintentar: 'إعادة المحاولة',
    tuTurno: 'رقمك',
    enEspera: 'في الانتظار',
    equipos: () => 'مجموعات أمامك',
    casiTurno: 'دورك قريبًا!',
    teUniste: 'انضممت',
    tuMesa: 'طاولتك',
    esperaAprox: (m) => `انتظار حوالي ${m} دقيقة`,
    reservadoPara: 'محجوز لـ',
    nPersonas: (n) => `${n} أشخاص`,
    aviso: 'التنبيهات',
    smsRespaldo: 'SMS احتياطي',
    tip: 'أبقِ هذه الشاشة في متناولك. سننبهك قبل بضع دقائق لتقترب.',
    salirFila: 'مغادرة الطابور',
    dlgTitle: 'مغادرة الطابور؟',
    dlgDesc: (t, n) => `ستفقد الرقم ${t} و${n} مقاعد محجوزة. سيتعين عليك التسجيل من جديد في نهاية الطابور.`,
    seguir: 'متابعة الانتظار',
    siSalir: 'نعم، غادر',
    esTuTurno: 'حان دورك!',
    mesaLista: 'طاولتك جاهزة. توجّه إلى المضيف عند المدخل.',
    turno: 'الرقم',
    llamado: 'تم النداء',
    personasLbl: 'أشخاص',
    veA: 'اذهب إلى',
    recepcion: 'الاستقبال · المدخل',
    guardamos: 'نحتفظ بمكانك',
    yaLlegue: 'لقد وصلت',
    noPodre: 'لن أتمكن من الحضور',
    bienvenido: 'أهلاً بك!',
    teAsignamos: (mesa, n) => [
      { text: 'خصّصنا لك ' },
      { text: `الطاولة ${mesa}`, strong: true },
      { text: ` لـ ${n} أشخاص.` },
    ],
    atendido: (t) => `الرقم ${t} · تم الجلوس`,
    verMenu: 'عرض القائمة',
    volverInicio: 'العودة إلى البداية',
    salisteFila: 'غادرت الطابور',
    liberamos: 'حرّرنا مكانك. يمكنك الانضمام مجددًا وقتما تشاء.',
    volverUnirme: 'الانضمام مجددًا',
    idioma: 'اللغة',
    gpsNote: 'سنتحقق من موقعك عند التأكيد.',
    gpsChecking: 'جارٍ التحقق من موقعك…',
    gpsFar: 'أنت بعيد جدًا عن المطعم. اقترب لتأكيد وصولك.',
    gpsUnavailable: 'تعذّر التحقق من موقعك. توجّه إلى المضيف عند المدخل.',
    cargando: 'جارٍ التحميل…',
    partyReadError: 'تعذّر تحميل حالتك في قائمة الانتظار.',
    partyReadStale: 'تعذّر التحديث. نعرض آخر حالة تم استلامها وسنحاول مجددًا.',
    chooseTitle: 'كيف تفضّل؟',
    chooseLead: 'اختر طريقتك المفضّلة اليوم.',
    modeTable: 'تناول في المطعم',
    modeTableDesc: 'انضم إلى الطابور للحصول على طاولة.',
    modePickup: 'طلب للاستلام',
    modePickupDesc: 'قف في الطابور للطلب عند الكاونتر.',
    pkJoinTitle: 'طابور الطلب',
    pkJoinLead: 'سننبّهك هنا وعبر WhatsApp عندما يحين دورك للطلب.',
    pkRefHelp: 'نحتفظ بمكانك. عند دورك ستطلب عند الكاونتر.',
    pkTuTurno: 'دورك',
    pkWaitBadge: 'في الطابور',
    pkEspera: (m) => `دورك خلال حوالي ${m} دقيقة`,
    pkReservado: 'الرقم',
    pkSubWaiting: (t) => `الرقم ${t} · في الطابور`,
    pkDlgDesc: (t) => `ستفقد الرقم ${t}. سيتعين عليك التسجيل من جديد في نهاية الطابور.`,
    pkReadyTitle: 'يمكنك الطلب الآن!',
    pkReadyLead: 'توجّه إلى الكاونتر واعرض رقمك لتقديم طلبك.',
    pkGoTo: 'كاونتر الطلب',
    pkGuardamos: 'نحتفظ بدورك',
    pkArrived: 'سأطلب',
    pkDoneTitle: 'جاهز للطلب!',
    pkNumLabel: 'رقمك',
    pkDoneLead: 'أظهر هذا الرقم للنادل لتقديم طلبك.',
    pkAtendido: (t) => `الرقم ${t} · تم`,
    pkOrderNumLabel: 'رقم الطلب',
    pkPrepTitle: 'تم تسجيل الطلب!',
    pkPrepLead: 'بدأ انتظار الاستلام. سننبّهك عبر WhatsApp عندما يجهز.',
    pkPrepBadge: 'قيد التحضير',
    pkPrepEta: (m) => `جاهز خلال حوالي ${m} دقيقة`,
    pkReady2Title: 'جاهز للاستلام!',
    pkReady2Lead: 'استلمه من الكاونتر مع إظهار رقمك.',
    pkCollect: 'استلمته',
    pkDeliveredTitle: 'بالهناء والشفاء!',
    pkDeliveredLead: 'شكرًا لطلبك.',
    menuDigital: 'قائمة رقمية',
    menuPdf: 'قائمة PDF',
    cancelar: 'إلغاء',
    verUbicacion: 'عرض الموقع',
    pkOrderInApp: 'اطلب عبر التطبيق',
    mcShowDetails: 'عرض التفاصيل',
    mcWithdrawLink: 'سحب الموافقة التسويقية',
    mcWithdrawTitle: 'هل تسحب موافقتك؟',
    mcWithdrawDesc: 'لن تصلك على هذا الرقم عروض وأخبار OLULO والمتاجر المشاركة عبر واتساب والرسائل النصية. يمكنك الموافقة مجددًا عند التسجيل في أي طابور. هذا لا يؤثر على مكانك في الطابور أو تنبيهات دورك.',
    mcWithdrawCta: 'نعم، اسحبها',
    mcWithdrawDone: 'تم. لن تصلك عروض OLULO على هذا الرقم بعد الآن.',
    mcWithdrawError: 'تعذّر سحب موافقتك. حاول مرة أخرى.',
    mcWithdrawUnavailable: 'لم نتمكن من معالجة سحب الموافقة الآن. راسلنا على hola@olulo.com.mx أو أرسل BAJA في محادثة WhatsApp.',
    storeFollowLabel: 'تلقّي عروض هذا المتجر',
    storeFollowOffHint: 'لن تصلك عروض هذا المتجر. ستستمر إشعارات دورك في الوصول.',
    storeFollowOnHint: 'ستصلك كوبونات وأخبار هذا المتجر على هذا الرقم.',
    storeFollowError: 'لم نتمكن من حفظ تفضيلك. حاول مرة أخرى.',
    storeFollowReconsent: 'لاستعادة عروض هذا المتجر، وافق على الإقرار عند تسجيلك في الطابور.',
  },
  vi: {
    subWaiting: (t, n) => `Số ${t} · ${n} người`,
    filaAbierta: 'Đang nhận khách',
    filaCerrada: 'Đã ngừng nhận khách',
    cerradaDesc: 'Hiện chúng tôi không nhận đăng ký. Vui lòng gặp nhân viên trực tiếp.',
    qrInactivo: 'Mã QR không hợp lệ',
    qrInactivoDesc: 'Mã này không còn hiệu lực. Hãy hỏi nhân viên mã hiện tại.',
    joinTitle: 'Vào hàng chờ',
    joinLead: 'Chờ ở bất cứ đâu. Chúng tôi sẽ báo tại đây và qua WhatsApp khi bàn của bạn sẵn sàng.',
    howMany: 'Mấy người?',
    personas: 'Số người',
    telefono: 'Điện thoại',
    telPh: '090 000 0000',
    telError: 'Nhập số điện thoại hợp lệ.',
    telHelp: 'Ưu tiên số có WhatsApp.',
    notifyNote: 'Chúng tôi sẽ báo qua WhatsApp. Nếu không gửi được, sẽ gửi SMS.',
    teamsInfo: (n, m) => [
      { text: `${n} nhóm`, bold: true },
      { text: ' đang chờ · khoảng ' },
      { text: `${m} phút`, bold: true },
    ],
    joinCta: 'Vào hàng chờ',
    joining: 'Đang đăng ký…',
    warnMax: (max) => `Tối đa ${max} người mỗi lần đăng ký. Nhóm lớn hơn vui lòng gặp nhân viên.`,
    duplicado: 'Số này đã có một đăng ký đang hoạt động.',
    errorGenerico: 'Không thể hoàn tất. Vui lòng thử lại.',
    reintentar: 'Thử lại',
    tuTurno: 'Số của bạn',
    enEspera: 'Đang chờ',
    equipos: () => 'nhóm trước bạn',
    casiTurno: 'Sắp đến lượt bạn!',
    teUniste: 'Đã vào hàng',
    tuMesa: 'Bàn của bạn',
    esperaAprox: (m) => `Chờ khoảng ${m} phút`,
    reservadoPara: 'Dành cho',
    nPersonas: (n) => `${n} người`,
    aviso: 'Thông báo',
    smsRespaldo: 'SMS dự phòng',
    tip: 'Hãy giữ màn hình này. Chúng tôi sẽ báo trước vài phút để bạn tới gần.',
    salirFila: 'Rời hàng chờ',
    dlgTitle: 'Rời hàng chờ?',
    dlgDesc: (t, n) => `Bạn sẽ mất số ${t} và ${n} chỗ đã giữ. Bạn phải đăng ký lại từ cuối hàng.`,
    seguir: 'Tiếp tục chờ',
    siSalir: 'Có, rời đi',
    esTuTurno: 'Đến lượt bạn!',
    mesaLista: 'Bàn của bạn đã sẵn sàng. Vui lòng gặp nhân viên ở lối vào.',
    turno: 'Số',
    llamado: 'Đã gọi',
    personasLbl: 'Số người',
    veA: 'Đi đến',
    recepcion: 'Quầy lễ tân · lối vào',
    guardamos: 'Chúng tôi đang giữ chỗ cho bạn',
    yaLlegue: 'Tôi đã đến',
    noPodre: 'Tôi không đến được',
    bienvenido: 'Chào mừng!',
    teAsignamos: (mesa, n) => [
      { text: 'Chúng tôi đã xếp ' },
      { text: `Bàn ${mesa}`, strong: true },
      { text: ` cho ${n} người.` },
    ],
    atendido: (t) => `Số ${t} · đã vào bàn`,
    verMenu: 'Xem thực đơn',
    volverInicio: 'Về đầu',
    salisteFila: 'Bạn đã rời hàng chờ',
    liberamos: 'Chúng tôi đã trả lại chỗ. Bạn có thể vào lại bất cứ lúc nào.',
    volverUnirme: 'Vào hàng chờ lại',
    idioma: 'Ngôn ngữ',
    gpsNote: 'Chúng tôi sẽ xác minh vị trí của bạn khi xác nhận.',
    gpsChecking: 'Đang kiểm tra vị trí…',
    gpsFar: 'Bạn ở quá xa nhà hàng. Hãy đến gần hơn để xác nhận.',
    gpsUnavailable: 'Không xác minh được vị trí của bạn. Vui lòng gặp nhân viên ở lối vào.',
    cargando: 'Đang tải…',
    partyReadError: 'Không thể tải trạng thái chờ của bạn.',
    partyReadStale: 'Không thể cập nhật. Chúng tôi đang hiển thị trạng thái nhận được gần nhất và sẽ thử lại.',
    chooseTitle: 'Bạn muốn thế nào?',
    chooseLead: 'Chọn cách bạn muốn hôm nay.',
    modeTable: 'Ăn tại chỗ',
    modeTableDesc: 'Vào hàng chờ bàn.',
    modePickup: 'Đặt mang đi',
    modePickupDesc: 'Xếp hàng để gọi món tại quầy.',
    pkJoinTitle: 'Hàng chờ gọi món',
    pkJoinLead: 'Chúng tôi sẽ báo tại đây và qua WhatsApp khi đến lượt bạn gọi món.',
    pkRefHelp: 'Chúng tôi giữ chỗ cho bạn. Đến lượt, bạn gọi món tại quầy.',
    pkTuTurno: 'Lượt của bạn',
    pkWaitBadge: 'Đang chờ',
    pkEspera: (m) => `Đến lượt sau khoảng ${m} phút`,
    pkReservado: 'Số',
    pkSubWaiting: (t) => `Số ${t} · đang chờ`,
    pkDlgDesc: (t) => `Bạn sẽ mất số ${t}. Bạn phải đăng ký lại từ cuối hàng.`,
    pkReadyTitle: 'Bạn có thể gọi món!',
    pkReadyLead: 'Đến quầy và xuất trình số của bạn để gọi món.',
    pkGoTo: 'Quầy gọi món',
    pkGuardamos: 'Chúng tôi giữ lượt cho bạn',
    pkArrived: 'Tôi đi gọi món',
    pkDoneTitle: 'Sẵn sàng gọi món!',
    pkNumLabel: 'Số của bạn',
    pkDoneLead: 'Xuất trình số này cho nhân viên để gọi món.',
    pkAtendido: (t) => `Lượt ${t} · xong`,
    pkOrderNumLabel: 'Số đơn hàng',
    pkPrepTitle: 'Đã đặt món!',
    pkPrepLead: 'Bắt đầu chờ lấy món. Chúng tôi sẽ báo qua WhatsApp khi sẵn sàng.',
    pkPrepBadge: 'Đang chuẩn bị',
    pkPrepEta: (m) => `Xong sau khoảng ${m} phút`,
    pkReady2Title: 'Sẵn sàng để lấy!',
    pkReady2Lead: 'Nhận tại quầy và xuất trình số của bạn.',
    pkCollect: 'Đã lấy món',
    pkDeliveredTitle: 'Chúc ngon miệng!',
    pkDeliveredLead: 'Cảm ơn bạn đã đặt món.',
    menuDigital: 'Thực đơn số',
    menuPdf: 'Thực đơn PDF',
    cancelar: 'Hủy',
    verUbicacion: 'Xem vị trí',
    pkOrderInApp: 'Đặt món trong ứng dụng',
    mcShowDetails: 'Xem chi tiết',
    mcWithdrawLink: 'Rút lại đồng ý tiếp thị',
    mcWithdrawTitle: 'Rút lại đồng ý của bạn?',
    mcWithdrawDesc: 'Số này sẽ không còn nhận khuyến mãi và tin mới của OLULO cùng các cửa hàng tham gia qua WhatsApp và SMS. Bạn có thể đồng ý lại khi đăng ký vào hàng chờ. Điều này không ảnh hưởng đến vị trí của bạn trong hàng chờ hoặc thông báo đến lượt.',
    mcWithdrawCta: 'Có, rút lại',
    mcWithdrawDone: 'Xong. Bạn sẽ không còn nhận khuyến mãi của OLULO trên số này.',
    mcWithdrawError: 'Chúng tôi không rút lại được đồng ý của bạn. Vui lòng thử lại.',
    mcWithdrawUnavailable: 'Hiện chúng tôi chưa xử lý được yêu cầu rút lại. Hãy viết cho chúng tôi qua hola@olulo.com.mx hoặc trả lời BAJA trong cuộc trò chuyện WhatsApp.',
    storeFollowLabel: 'Nhận khuyến mãi từ cửa hàng này',
    storeFollowOffHint: 'Bạn sẽ không nhận khuyến mãi từ cửa hàng này. Thông báo đến lượt vẫn được gửi như thường.',
    storeFollowOnHint: 'Bạn sẽ nhận phiếu giảm giá và tin mới của cửa hàng này qua số điện thoại này.',
    storeFollowError: 'Chúng tôi chưa lưu được tùy chọn của bạn. Hãy thử lại.',
    storeFollowReconsent: 'Để nhận lại khuyến mãi từ cửa hàng này, hãy đồng ý khi đăng ký xếp hàng.',
  },
  th: {
    subWaiting: (t, n) => `คิว ${t} · ${n} คน`,
    filaAbierta: 'เปิดรับคิว',
    filaCerrada: 'ปิดรับคิว',
    cerradaDesc: 'ขณะนี้ไม่เปิดรับลงทะเบียน กรุณาติดต่อพนักงานโดยตรง',
    qrInactivo: 'คิวอาร์โค้ดไม่ถูกต้อง',
    qrInactivoDesc: 'โค้ดนี้ใช้ไม่ได้แล้ว กรุณาขอโค้ดปัจจุบันจากพนักงาน',
    joinTitle: 'เข้าคิวรอ',
    joinLead: 'รอจากที่ไหนก็ได้ เราจะแจ้งที่นี่และทาง WhatsApp เมื่อโต๊ะของคุณพร้อม',
    howMany: 'กี่ท่าน?',
    personas: 'จำนวนคน',
    telefono: 'โทรศัพท์',
    telPh: '08 0000 0000',
    telError: 'กรอกหมายเลขโทรศัพท์ที่ถูกต้อง',
    telHelp: 'ควรเป็นเบอร์ที่มี WhatsApp',
    notifyNote: 'เราจะแจ้งทาง WhatsApp หากส่งไม่สำเร็จจะส่ง SMS แทน',
    teamsInfo: (n, m) => [
      { text: `${n} กลุ่ม`, bold: true },
      { text: ' กำลังรอ · ประมาณ ' },
      { text: `${m} นาที`, bold: true },
    ],
    joinCta: 'เข้าคิวรอ',
    joining: 'กำลังลงทะเบียน…',
    warnMax: (max) => `ลงทะเบียนได้สูงสุด ${max} คนต่อครั้ง กลุ่มที่ใหญ่กว่านี้กรุณาติดต่อพนักงานโดยตรง`,
    duplicado: 'เบอร์นี้มีการลงทะเบียนที่ยังใช้งานอยู่แล้ว',
    errorGenerico: 'ทำรายการไม่สำเร็จ กรุณาลองใหม่',
    reintentar: 'ลองใหม่',
    tuTurno: 'คิวของคุณ',
    enEspera: 'กำลังรอ',
    equipos: () => 'กลุ่มอยู่ก่อนคุณ',
    casiTurno: 'ใกล้ถึงคิวคุณแล้ว!',
    teUniste: 'เข้าคิวแล้ว',
    tuMesa: 'โต๊ะของคุณ',
    esperaAprox: (m) => `รอประมาณ ${m} นาที`,
    reservadoPara: 'จองสำหรับ',
    nPersonas: (n) => `${n} คน`,
    aviso: 'การแจ้งเตือน',
    smsRespaldo: 'SMS สำรอง',
    tip: 'โปรดเปิดหน้าจอนี้ไว้ เราจะแจ้งล่วงหน้าไม่กี่นาทีเพื่อให้คุณเข้ามา',
    salirFila: 'ออกจากคิว',
    dlgTitle: 'ออกจากคิว?',
    dlgDesc: (t, n) => `คุณจะเสียคิว ${t} และที่นั่งที่จองไว้ ${n} ที่ ต้องลงทะเบียนใหม่ท้ายแถว`,
    seguir: 'รอต่อไป',
    siSalir: 'ใช่ ออกเลย',
    esTuTurno: 'ถึงคิวคุณแล้ว!',
    mesaLista: 'โต๊ะของคุณพร้อมแล้ว กรุณาแจ้งพนักงานต้อนรับที่ทางเข้า',
    turno: 'คิว',
    llamado: 'เรียกแล้ว',
    personasLbl: 'จำนวนคน',
    veA: 'ไปที่',
    recepcion: 'แผนกต้อนรับ · ทางเข้า',
    guardamos: 'เรากำลังกันที่ให้คุณ',
    yaLlegue: 'ฉันมาถึงแล้ว',
    noPodre: 'ฉันมาไม่ได้',
    bienvenido: 'ยินดีต้อนรับ!',
    teAsignamos: (mesa, n) => [
      { text: 'เราจัด' },
      { text: `โต๊ะ ${mesa}`, strong: true },
      { text: `ให้คุณสำหรับ ${n} คน` },
    ],
    atendido: (t) => `คิว ${t} · เข้านั่งแล้ว`,
    verMenu: 'ดูเมนู',
    volverInicio: 'กลับไปหน้าแรก',
    salisteFila: 'คุณออกจากคิวแล้ว',
    liberamos: 'เราปล่อยที่ของคุณแล้ว คุณกลับมาเข้าคิวได้ทุกเมื่อ',
    volverUnirme: 'เข้าคิวอีกครั้ง',
    idioma: 'ภาษา',
    gpsNote: 'เราจะยืนยันตำแหน่งของคุณเมื่อกดยืนยัน',
    gpsChecking: 'กำลังตรวจสอบตำแหน่ง…',
    gpsFar: 'คุณอยู่ไกลจากร้านเกินไป กรุณาเข้าใกล้เพื่อยืนยันการมาถึง',
    gpsUnavailable: 'ตรวจสอบตำแหน่งของคุณไม่ได้ กรุณาแจ้งพนักงานต้อนรับที่ทางเข้า',
    cargando: 'กำลังโหลด…',
    partyReadError: 'โหลดสถานะคิวของคุณไม่สำเร็จ',
    partyReadStale: 'อัปเดตไม่สำเร็จ กำลังแสดงสถานะล่าสุดที่ได้รับ และจะลองอีกครั้ง',
    chooseTitle: 'คุณสะดวกแบบไหน?',
    chooseLead: 'เลือกวิธีที่คุณต้องการวันนี้',
    modeTable: 'ทานที่ร้าน',
    modeTableDesc: 'เข้าคิวรอโต๊ะ',
    modePickup: 'สั่งกลับบ้าน',
    modePickupDesc: 'เข้าคิวเพื่อสั่งที่เคาน์เตอร์',
    pkJoinTitle: 'คิวสั่งอาหาร',
    pkJoinLead: 'เมื่อถึงคิวสั่งของคุณ เราจะแจ้งที่นี่และทาง WhatsApp',
    pkRefHelp: 'เราเก็บคิวให้คุณ เมื่อถึงคิวคุณสามารถสั่งที่เคาน์เตอร์ได้',
    pkTuTurno: 'คิวของคุณ',
    pkWaitBadge: 'อยู่ในคิว',
    pkEspera: (m) => `ถึงคิวในอีกประมาณ ${m} นาที`,
    pkReservado: 'คิว',
    pkSubWaiting: (t) => `คิว ${t} · อยู่ในคิว`,
    pkDlgDesc: (t) => `คุณจะเสียคิว ${t} ต้องลงทะเบียนใหม่ท้ายแถว`,
    pkReadyTitle: 'สั่งอาหารได้แล้ว!',
    pkReadyLead: 'ไปที่เคาน์เตอร์และแสดงคิวของคุณเพื่อสั่งอาหาร',
    pkGoTo: 'เคาน์เตอร์สั่งอาหาร',
    pkGuardamos: 'เราเก็บคิวให้คุณ',
    pkArrived: 'ไปสั่งอาหาร',
    pkDoneTitle: 'พร้อมสั่งอาหารแล้ว!',
    pkNumLabel: 'หมายเลขของคุณ',
    pkDoneLead: 'แสดงหมายเลขนี้แก่พนักงานเพื่อสั่งอาหาร',
    pkAtendido: (t) => `คิว ${t} · เสร็จ`,
    pkOrderNumLabel: 'หมายเลขออร์เดอร์',
    pkPrepTitle: 'สั่งอาหารแล้ว!',
    pkPrepLead: 'เริ่มรอรับอาหาร เมื่อพร้อมเราจะแจ้งทาง WhatsApp',
    pkPrepBadge: 'กำลังเตรียม',
    pkPrepEta: (m) => `เสร็จในอีกประมาณ ${m} นาที`,
    pkReady2Title: 'พร้อมรับแล้ว!',
    pkReady2Lead: 'รับที่เคาน์เตอร์พร้อมแสดงหมายเลข',
    pkCollect: 'รับแล้ว',
    pkDeliveredTitle: 'ทานให้อร่อย!',
    pkDeliveredLead: 'ขอบคุณที่สั่ง',
    menuDigital: 'เมนูดิจิทัล',
    menuPdf: 'เมนู PDF',
    cancelar: 'ยกเลิก',
    verUbicacion: 'ดูตำแหน่งที่ตั้ง',
    pkOrderInApp: 'สั่งอาหารในแอป',
    mcShowDetails: 'ดูรายละเอียด',
    mcWithdrawLink: 'ถอนความยินยอมด้านการตลาด',
    mcWithdrawTitle: 'ถอนความยินยอมของคุณไหม',
    mcWithdrawDesc: 'หมายเลขนี้จะไม่ได้รับโปรโมชันและข่าวสารของ OLULO และร้านค้าที่ร่วมรายการทาง WhatsApp และ SMS อีก คุณยอมรับใหม่ได้เมื่อลงทะเบียนเข้าคิวครั้งถัดไป การดำเนินการนี้ไม่มีผลต่อลำดับคิวหรือการแจ้งเตือนเมื่อถึงคิวของคุณ',
    mcWithdrawCta: 'ใช่ ถอนเลย',
    mcWithdrawDone: 'เรียบร้อย คุณจะไม่ได้รับโปรโมชันของ OLULO ที่หมายเลขนี้อีก',
    mcWithdrawError: 'เราถอนความยินยอมของคุณไม่สำเร็จ กรุณาลองใหม่',
    mcWithdrawUnavailable: 'ขณะนี้เรายังดำเนินการเพิกถอนให้ไม่ได้ กรุณาเขียนถึงเราที่ hola@olulo.com.mx หรือตอบกลับ BAJA ในแชต WhatsApp',
    storeFollowLabel: 'รับโปรโมชันจากร้านนี้',
    storeFollowOffHint: 'คุณจะไม่ได้รับโปรโมชันจากร้านนี้ แต่ยังได้รับแจ้งเตือนคิวของคุณตามปกติ',
    storeFollowOnHint: 'คุณจะได้รับคูปองและข่าวสารของร้านนี้ทางหมายเลขนี้',
    storeFollowError: 'เราบันทึกการตั้งค่าของคุณไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
    storeFollowReconsent: 'หากต้องการรับโปรโมชันจากร้านนี้อีกครั้ง กรุณายอมรับความยินยอมตอนลงทะเบียนเข้าคิว',
  },
  nl: {
    subWaiting: (t, n) => `Nummer ${t} · ${n} personen`,
    filaAbierta: 'Wachtrij open',
    filaCerrada: 'Wachtrij gesloten',
    cerradaDesc: 'We nemen op dit moment geen aanmeldingen aan. Meld je bij de gastheer.',
    qrInactivo: 'Ongeldige QR-code',
    qrInactivoDesc: 'Deze code is niet meer actief. Vraag de gastheer om de huidige code.',
    joinTitle: 'Sluit aan in de rij',
    joinLead: 'Wacht vanaf elke plek. We laten het hier en via WhatsApp weten zodra je tafel klaar is.',
    howMany: 'Met hoeveel personen?',
    personas: 'Personen',
    telefono: 'Telefoon',
    telPh: '06 0000 0000',
    telError: 'Voer een geldig telefoonnummer in.',
    telHelp: 'Bij voorkeur een nummer met WhatsApp.',
    notifyNote: 'We waarschuwen je via WhatsApp. Lukt dat niet, dan sturen we een sms.',
    teamsInfo: (n, m) => [
      { text: `${n} groepen`, bold: true },
      { text: ' in de rij · ca. ' },
      { text: `${m} min`, bold: true },
    ],
    joinCta: 'Aansluiten',
    joining: 'Aanmelden…',
    warnMax: (max) => `Maximaal ${max} personen per aanmelding. Voor grotere groepen: meld je bij de gastheer.`,
    duplicado: 'Je hebt al een actieve aanmelding met dit nummer.',
    errorGenerico: 'Dat is niet gelukt. Probeer het opnieuw.',
    reintentar: 'Opnieuw proberen',
    tuTurno: 'Jouw nummer',
    enEspera: 'Wachtend',
    equipos: (n) => (n === 1 ? 'groep voor je' : 'groepen voor je'),
    casiTurno: 'Bijna aan de beurt!',
    teUniste: 'Je bent aangesloten',
    tuMesa: 'Jouw tafel',
    esperaAprox: (m) => `Wachttijd ca. ${m} min`,
    reservadoPara: 'Gereserveerd voor',
    nPersonas: (n) => `${n} personen`,
    aviso: 'Meldingen',
    smsRespaldo: 'Sms als back-up',
    tip: 'Houd dit scherm bij de hand. We waarschuwen je een paar minuten van tevoren.',
    salirFila: 'Rij verlaten',
    dlgTitle: 'Rij verlaten?',
    dlgDesc: (t, n) => `Je verliest nummer ${t} en ${n} gereserveerde plaatsen. Je moet je achteraan opnieuw aanmelden.`,
    seguir: 'Blijven wachten',
    siSalir: 'Ja, verlaten',
    esTuTurno: 'Je bent aan de beurt!',
    mesaLista: 'Je tafel is klaar. Meld je bij de gastheer bij de ingang.',
    turno: 'Nummer',
    llamado: 'Opgeroepen',
    personasLbl: 'Personen',
    veA: 'Ga naar',
    recepcion: 'Receptie · ingang',
    guardamos: 'We houden je plek vast',
    yaLlegue: 'Ik ben er',
    noPodre: 'Ik red het niet',
    bienvenido: 'Welkom!',
    teAsignamos: (mesa, n) => [
      { text: 'We hebben je ' },
      { text: `Tafel ${mesa}`, strong: true },
      { text: ` toegewezen voor ${n} personen.` },
    ],
    atendido: (t) => `Nummer ${t} · geplaatst`,
    verMenu: 'Bekijk het menu',
    volverInicio: 'Terug naar start',
    salisteFila: 'Je hebt de rij verlaten',
    liberamos: 'We hebben je plek vrijgegeven. Je kunt altijd weer aansluiten.',
    volverUnirme: 'Opnieuw aansluiten',
    idioma: 'Taal',
    gpsNote: 'We verifiëren je locatie bij het bevestigen.',
    gpsChecking: 'Locatie controleren…',
    gpsFar: 'Je bent te ver van het restaurant. Kom dichterbij om je aankomst te bevestigen.',
    gpsUnavailable: 'We konden je locatie niet verifiëren. Meld je bij de gastheer bij de ingang.',
    cargando: 'Laden…',
    partyReadError: 'Je wachtstatus kon niet worden geladen.',
    partyReadStale: 'Bijwerken is niet gelukt. We tonen de laatst ontvangen status en proberen het opnieuw.',
    chooseTitle: 'Hoe wil je het?',
    chooseLead: 'Kies hoe je het vandaag wilt.',
    modeTable: 'Ter plaatse eten',
    modeTableDesc: 'Sluit aan in de rij voor een tafel.',
    modePickup: 'Afhaal bestellen',
    modePickupDesc: 'Sluit aan om aan de balie te bestellen.',
    pkJoinTitle: 'Bestelrij',
    pkJoinLead: 'We laten het hier en via WhatsApp weten wanneer je aan de beurt bent om te bestellen.',
    pkRefHelp: 'We houden je plek vast. Als je aan de beurt bent, bestel je aan de balie.',
    pkTuTurno: 'Jouw beurt',
    pkWaitBadge: 'In de rij',
    pkEspera: (m) => `Aan de beurt over ca. ${m} min`,
    pkReservado: 'Nummer',
    pkSubWaiting: (t) => `Nummer ${t} · in de rij`,
    pkDlgDesc: (t) => `Je verliest nummer ${t}. Je moet je achteraan opnieuw aanmelden.`,
    pkReadyTitle: 'Je kunt nu bestellen!',
    pkReadyLead: 'Ga naar de balie en toon je nummer om te bestellen.',
    pkGoTo: 'Bestelbalie',
    pkGuardamos: 'We houden je beurt vast',
    pkArrived: 'Ik ga bestellen',
    pkDoneTitle: 'Klaar om te bestellen!',
    pkNumLabel: 'Je nummer',
    pkDoneLead: 'Toon dit nummer aan de medewerker om te bestellen.',
    pkAtendido: (t) => `Nummer ${t} · klaar`,
    pkOrderNumLabel: 'Bestelnummer',
    pkPrepTitle: 'Bestelling geplaatst!',
    pkPrepLead: 'Het afhaalwachten is begonnen. We laten via WhatsApp weten wanneer het klaar is.',
    pkPrepBadge: 'In bereiding',
    pkPrepEta: (m) => `Klaar over ca. ${m} min`,
    pkReady2Title: 'Klaar om af te halen!',
    pkReady2Lead: 'Haal het op aan de balie en toon je nummer.',
    pkCollect: 'Opgehaald',
    pkDeliveredTitle: 'Eet smakelijk!',
    pkDeliveredLead: 'Bedankt voor je bestelling.',
    menuDigital: 'Digitaal menu',
    menuPdf: 'PDF-menu',
    cancelar: 'Annuleren',
    verUbicacion: 'Locatie bekijken',
    pkOrderInApp: 'Bestellen in de app',
    mcShowDetails: 'Details bekijken',
    mcWithdrawLink: 'Marketingtoestemming intrekken',
    mcWithdrawTitle: 'Je toestemming intrekken?',
    mcWithdrawDesc: 'Je ontvangt op dit nummer geen acties en nieuws meer van OLULO en de deelnemende zaken via WhatsApp en sms. Je kunt ze weer accepteren wanneer je je aanmeldt voor een wachtrij. Dit heeft geen invloed op je plek in de wachtrij of de melding wanneer je aan de beurt bent.',
    mcWithdrawCta: 'Ja, intrekken',
    mcWithdrawDone: 'Klaar. Je ontvangt op dit nummer geen OLULO-acties meer.',
    mcWithdrawError: 'We konden je toestemming niet intrekken. Probeer het opnieuw.',
    mcWithdrawUnavailable: 'We konden je intrekking nu niet verwerken. Mail ons op hola@olulo.com.mx of antwoord BAJA in de WhatsApp-chat.',
    storeFollowLabel: 'Promoties van deze zaak ontvangen',
    storeFollowOffHint: 'Je ontvangt geen promoties van deze zaak. Meldingen over je beurt blijven gewoon komen.',
    storeFollowOnHint: 'Je ontvangt kortingsbonnen en nieuws van deze zaak op dit nummer.',
    storeFollowError: 'We konden je voorkeur niet opslaan. Probeer het opnieuw.',
    storeFollowReconsent: 'Wil je weer promoties van deze zaak ontvangen? Ga akkoord bij je aanmelding in de rij.',
  },
};

/**
 * 전화번호 표시 포맷 — 디자인 원본 `fmtPhone` 이식.
 *
 * 멕시코(+52)는 `55 1834 2201` 관습 표기를, 그 외는 3자리 그룹 구분을 적용한다.
 * 서버에는 구분자를 제거한 숫자열만 보낸다 (@see toPhoneDigits).
 */
export function formatWaitlistPhone(value: string, dialCode: string): string {
  let digits = value.replace(/\D/g, '');

  if (dialCode === '+52') {
    digits = digits.slice(0, digits[0] === '0' ? 11 : 10);
    const front = digits[0] === '0' ? 3 : 2;
    if (digits.length <= front) {
      return digits;
    }
    const rest = digits.slice(front);
    if (rest.length <= 4) {
      return `${digits.slice(0, front)} ${rest}`;
    }
    return `${digits.slice(0, front)} ${rest.slice(0, rest.length - 4)} ${rest.slice(-4)}`;
  }

  return digits.slice(0, 16).replace(/(\d{3})(?=\d)/g, '$1 ').trim();
}

/**
 * 서버 전송용 숫자열 — 표시 구분자 제거 + 선행 0(국내 트렁크 프리픽스) 제거.
 *
 * 선행 0 은 국제 형식(E.164)에서 빠지므로 남기면 서버 합성 번호가 어긋난다.
 */
export function toPhoneDigits(value: string): string {
  return value.replace(/\D/g, '').replace(/^0/, '');
}

/** 국가별 자리수 검증 — 디자인 원본 `phoneOk` 이식. */
export function isValidWaitlistPhone(value: string, country: WaitlistCountry): boolean {
  const core = toPhoneDigits(value);

  if (country.len !== null) {
    return core.length === country.len;
  }

  return core.length >= VARIABLE_PHONE_MIN_DIGITS && core.length <= VARIABLE_PHONE_MAX_DIGITS;
}

/** dial code → 국가 정보. 미지원 코드는 기본(멕시코)로 폴백. */
export function findWaitlistCountry(dialCode: string): WaitlistCountry {
  return WAITLIST_COUNTRIES.find((c) => c.dial === dialCode) ?? WAITLIST_COUNTRIES[0];
}
